import React, { useState, useEffect, useRef, useMemo, useCallback, MouseEvent } from 'react';
import type { OrderItem, MenuItem as MenuItemType, Category, Customer, OrderType, Table, LoyaltyReward, Order, QuickAddItem, OrderStatus, Payment, SmartpayTerminal, RegisterSession, Reservation, StaffMember, InventoryItem, Recipe, FloorFixture, IntegrationSettings, DeliveryOrder } from '../types';
import { getAvailableQuantity } from '../lib/dailyAvailability';
import { getOrderTotals, getOrderTotalFromItems, hasBalanceDue, orderHasMeaningfulItems, getTableSeatsUsed, isPendingPosOrder, isCompletedPosOrder, isPosOrderFullySettled, isUnpaidOnlineWebOrder, isFloorPlanActiveTableOrder } from '../lib/orderUtils';
import { isSampleOrDemoOrderId } from '../lib/sampleOrderIds';
import { orderCreatedAtMs, orderSaleAtMs, orderSaleDateKey, ymdInTimeZone, ymdToEndMs, ymdToStartMs, addDaysToYmd } from '../lib/orderTimestamps';
import { auditLogsService, couponCampaignsService, couponsService, couponRedemptionsService, couponUsageLogsService, ordersService } from '../lib/database';
import { markUberEatsOrderReady, reportUberOrderIssue } from '../lib/uberEatsAuth';
import CategoryList from '../components/pos/CategoryList';
import MenuItemsGrid from '../components/pos/MenuItemsGrid';
import OrderSummary from '../components/pos/OrderSummary';
import POSHeader from '../components/pos/POSHeader';
import CustomerSelectModal from '../components/pos/CustomerSelectModal';
import RewardRedemptionModal from '../components/pos/RewardRedemptionModal';
import PrintableReceipt from '../components/pos/PrintableReceipt';
import PrintableKitchenOrder from '../components/pos/PrintableKitchenOrder';
import PrintTypeModal from '../components/pos/PrintTypeModal';
import PaymentModal from './PaymentModal';
import QuickAddBar from '../components/pos/QuickAddBar';
import CustomerConfirmationDisplay from '../components/pos/CustomerConfirmationDisplay';
import RegisterClosedGuard from '../components/pos/RegisterClosedGuard';
import type { QuickAddCombo } from '../types';
import FloorPlanView from '../components/pos/FloorPlanView';
import OrderHistory from '../components/pos/OrderHistory';
import OrderDetailsModal from '../components/pos/OrderDetailsModal';
import OrderTypeSelector from '../components/pos/OrderTypeSelector';
import DiscountModal from '../components/pos/DiscountModal';
import ManagerApprovalModal from '../components/pos/ManagerApprovalModal';
import CancelOrderApprovalModal from '../components/pos/CancelOrderApprovalModal';
import MeatOptionModal from '../components/pos/MeatOptionModal';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import TableMergeModal from '../components/pos/TableMergeModal';
import TableSplitModal from '../components/pos/TableSplitModal';
import OrderTransferModal from '../components/pos/OrderTransferModal';
import OrderOverrideModal from '../components/pos/OrderOverrideModal';
import FireModeModal from '../components/pos/FireModeModal';
import FireModeBanner from '../components/pos/FireModeBanner';
import RemakeOrderModal from '../components/pos/RemakeOrderModal';
import TableCustomerSelectModal from '../components/pos/TableCustomerSelectModal';
import DineInCoversModal from '../components/pos/DineInCoversModal';
import BuffetTypeSelector from '../components/pos/BuffetTypeSelector';
import UberDoordashOrderModal from '../components/pos/UberDoordashOrderModal';
import CustomerNameModal from '../components/pos/CustomerNameModal';
import BuffetExtensionModal from '../components/pos/BuffetExtensionModal';
import BuffetTimer from '../components/pos/BuffetTimer';
import BuffetPeopleModal from '../components/pos/BuffetPeopleModal';
import { buildEventTokenLabelById, formatEventTokenShortBadge, getEventTokenDisplayLabel } from '../lib/eventOrderTokens';

const DRAFT_ORDER_KEY = 'kings_park_pos_draft_order';
const POS_VIEW_KEY = 'kings_park_pos_view';
const POS_ACTIVE_VIEW_KEY = 'kings_park_pos_active_view';

type PosViewType = 'active-orders' | 'floor' | 'new-order' | 'pending' | 'completed' | 'cancelled' | 'refunded';
const VALID_POS_VIEWS: PosViewType[] = ['active-orders', 'floor', 'new-order', 'pending', 'completed', 'cancelled', 'refunded'];
type ActiveViewType = 'floor' | 'menu' | 'history';
const VALID_ACTIVE_VIEWS: ActiveViewType[] = ['floor', 'menu', 'history'];

/** Pending/Completed cards: fill horizontal space; column count follows viewport width. */
const POS_ORDER_LIST_GRID_CLASS =
  'grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,260px),1fr))]';
const POS_ORDER_LIST_SECTION_CLASS = 'w-full space-y-3';

type HistoryOrdersDateFilter = 'today' | 'yesterday' | 'weekly' | 'custom';

function mergeOrderSources(...lists: Order[][]): Order[] {
  const map = new Map<string, Order>();
  for (const list of lists) {
    for (const order of list) {
      map.set(order.id, order);
    }
  }
  return Array.from(map.values());
}

function filterOrdersByHistoryDate(
  orders: Order[],
  filter: HistoryOrdersDateFilter,
  opts: {
    todayKey: string;
    yesterdayKey: string;
    weekStartYmd: string;
    customDateStart: string;
    customDateEnd: string;
    getOrderDateKey: (order: Order) => string;
  },
): Order[] {
  switch (filter) {
    case 'today':
      return orders.filter((o) => opts.getOrderDateKey(o) === opts.todayKey);
    case 'yesterday':
      return orders.filter((o) => opts.getOrderDateKey(o) === opts.yesterdayKey);
    case 'weekly':
      return orders.filter((o) => {
        const d = opts.getOrderDateKey(o);
        return d >= opts.weekStartYmd && d <= opts.todayKey;
      });
    case 'custom':
      if (!opts.customDateStart || !opts.customDateEnd) return [];
      return orders.filter((o) => {
        const d = opts.getOrderDateKey(o);
        return d >= opts.customDateStart && d <= opts.customDateEnd;
      });
    default:
      return orders;
  }
}

const HistoryOrdersDateFilterBar: React.FC<{
  filter: HistoryOrdersDateFilter;
  onFilterChange: (filter: HistoryOrdersDateFilter) => void;
  customDateStart: string;
  customDateEnd: string;
  onCustomDateStartChange: (value: string) => void;
  onCustomDateEndChange: (value: string) => void;
  extraControls?: React.ReactNode;
}> = ({
  filter,
  onFilterChange,
  customDateStart,
  customDateEnd,
  onCustomDateStartChange,
  onCustomDateEndChange,
  extraControls,
}) => (
  <div className="flex flex-wrap gap-2 items-center">
    {extraControls}
    <div className={`flex gap-2 ${extraControls ? 'ml-2' : ''}`}>
      {(['today', 'yesterday', 'weekly', 'custom'] as const).map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onFilterChange(key)}
          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
            filter === key ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {key === 'today' ? 'Today' : key === 'yesterday' ? 'Yesterday' : key === 'weekly' ? 'Week' : 'By Date'}
        </button>
      ))}
    </div>
    {filter === 'custom' && (
      <div className="flex gap-2 items-center">
        <input
          type="date"
          value={customDateStart}
          onChange={(e) => onCustomDateStartChange(e.target.value)}
          className="px-2 py-1 text-sm border border-gray-300 rounded-md"
          aria-label="Start date"
        />
        <span className="text-gray-500">to</span>
        <input
          type="date"
          value={customDateEnd}
          onChange={(e) => onCustomDateEndChange(e.target.value)}
          className="px-2 py-1 text-sm border border-gray-300 rounded-md"
          aria-label="End date"
        />
      </div>
    )}
  </div>
);

function getStoredPosView(): PosViewType | null {
  try {
    const stored = sessionStorage.getItem(POS_VIEW_KEY);
    if (stored && VALID_POS_VIEWS.includes(stored as PosViewType)) return stored as PosViewType;
  } catch (_) {}
  return null;
}
function getStoredActiveView(): ActiveViewType | null {
  try {
    const stored = sessionStorage.getItem(POS_ACTIVE_VIEW_KEY);
    if (stored && VALID_ACTIVE_VIEWS.includes(stored as ActiveViewType)) return stored as ActiveViewType;
  } catch (_) {}
  return null;
}

interface POSPageProps {
    onPlaceOrder: (items: OrderItem[], orderType: OrderType, details: { tableId?: number, customerId?: number, customerNumber?: number, customerName?: string, notes?: string, appliedReward?: LoyaltyReward, manualDiscount?: number, discountRequiresApproval?: boolean, discountApprovedBy?: number, discountApprovalReason?: string, discountApprovalTimestamp?: number, buffetType?: 'breakfast' | 'lunch' | 'dinner', buffetStartTime?: number, buffetEndTime?: number, servingStaffId?: number, servingStaffName?: string, deliveryInfo?: { platform: 'Uber Eats' | 'DoorDash'; customerName: string }, couponCode?: string, couponCampaignId?: string, couponDiscountType?: 'percentage' | 'fixed', couponDiscountValue?: number, couponDiscountAmount?: number, couponAppliedByStaffId?: number, couponAppliedAt?: number, couponUnitLabel?: string, couponBuildingWing?: 'hotel_wing' | 'tower_wing', residentDiscountPercent?: number, residentDiscountAmount?: number, deliveryCharge?: number }) => Order | void;
    onUpdateOrder: (order: Order) => void;
    onRemoveOrder?: (orderId: string) => Promise<void>;
    onProcessPayment: (orderId: string, payment: Payment) => void;
    /** Manager-only: refund a single closed sub-bill (restores paidQuantity, writes negative payment record). */
    onRefundSubBill?: (orderId: string, subBillId: string) => Promise<void> | void;
    onMarkAsPaid?: (orderId: string) => void;
    menuItems: MenuItemType[];
    categories: Category[];
    customers: Customer[];
    tables: Table[];
    layoutComponents?: FloorFixture[];
    rewards: LoyaltyReward[];
    orders: Order[];
    reservations?: Reservation[];
    quickAddCombos?: QuickAddCombo[];
    terminals: SmartpayTerminal[];
    registerSession: RegisterSession | undefined;
    activeSessions?: RegisterSession[];
    /**
     * False until the global `subscribeActive` listener in App.tsx has delivered
     * its first snapshot. Forwarded to RegisterClosedGuard so the spinner is
     * shown instead of the Open Register button while we don't know whether
     * another device has already opened a shared session.
     */
    registerSessionsHydrated?: boolean;
    terminalId?: string;
    terminalName?: string;
    onTerminalNameChange?: (name: string) => void;
    onOpenRegister: (openingFloat: number) => void;
    onCloseRegister: (
      closingFloat: number,
      session: RegisterSession,
      todaysSales: { cash: number; card: number; cashDenominations?: { denom: number; count: number }[] },
    ) => void;
    onForceCloseSession?: (session: RegisterSession) => void;
    showForceClose?: boolean;
    onMoveCategory?: (categoryId: number | string, direction: 'up' | 'down') => void;
    onReorderCategory?: (categoryId: number | string, targetIndex: number) => void;
    staffMembers?: StaffMember[];
    currentUser?: StaffMember;
    inventoryItems?: InventoryItem[];
    recipes?: Recipe[];
    onStockTake?: (physicalCounts: { [itemId: number]: number }) => void;
    onRequireManagerPin: (action: 'open' | 'close', onApproved: () => void) => void;
    integrationSettings?: IntegrationSettings;
    onUpdateMenuItem?: (itemId: number, updates: Partial<MenuItemType>) => Promise<void>;
    onMergeTables?: (tableIds: number[]) => Promise<void>;
    onSplitTables?: (tableId: number) => Promise<void>;
    onTransferOrder?: (orderId: string, targetTableId: number) => Promise<void>;
    onOverrideOrder?: (orderId: string, reason: string) => Promise<void>;
    onRemakeOrder?: (orderId: string, items: OrderItem[], reason: string) => Promise<void>;
    onExtendBuffetTime?: (orderId: string, extensionMinutes: number, reason?: string) => Promise<void>;
    fireModeActive?: boolean;
    onActivateFireMode?: () => void;
    onDeactivateFireMode?: () => void;
    onRequireManagerPin: (action: 'open' | 'close', onApproved: () => void) => void;
    onCreateCustomer?: (customer: Omit<Customer, 'id'>) => Promise<Customer>;
    onAuditPrint?: (type: 'customer' | 'kitchen', order: Order) => void;
    /** Optional: update order status (e.g. for Uber/DoorDash ready / picked up). */
    onUpdateOrderStatus?: (orderId: string, status: OrderStatus) => void;
    /** Incoming third-party delivery orders (Uber/DoorDash) that are waiting for acceptance. */
    uberPendingOrders?: DeliveryOrder[];
    /** Controls Uber incoming chime playback in POS (disable outside POS screens). */
    enableUberIncomingAlert?: boolean;
    onAcceptUberDeliveryOrder?: (order: DeliveryOrder) => void;
    onRejectUberDeliveryOrder?: (order: DeliveryOrder) => void;
}

const POSPage: React.FC<POSPageProps> = ({ 
    onPlaceOrder, 
    onUpdateOrder,
    onRemoveOrder,
    onProcessPayment,
    onRefundSubBill,
    onMarkAsPaid,
    menuItems,
    categories, 
    customers, 
    tables, 
    rewards, 
    orders, 
    reservations = [],
    quickAddCombos = [],
    terminals,
    layoutComponents = [],
    registerSession,
    onCreateCustomer,
    activeSessions = [],
    registerSessionsHydrated = true,
    terminalId = '',
    terminalName = '',
    onTerminalNameChange,
    onOpenRegister,
    onCloseRegister,
    onForceCloseSession,
    showForceClose = false,
    onMoveCategory,
    onReorderCategory,
    staffMembers = [],
    currentUser,
    inventoryItems = [],
    recipes = [],
    onStockTake,
    integrationSettings,
    onUpdateMenuItem,
    onMergeTables,
    onSplitTables,
    onTransferOrder,
    onOverrideOrder,
    onRemakeOrder,
    onExtendBuffetTime,
    onAuditPrint,
    fireModeActive = false,
    onActivateFireMode,
    onDeactivateFireMode,
    onRequireManagerPin,
    uberPendingOrders = [],
    enableUberIncomingAlert = true,
    onAcceptUberDeliveryOrder,
    onRejectUberDeliveryOrder,
    onUpdateOrderStatus,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  
  // Update selectedCategoryId when categories load (category id can be number or Firestore string)
  useEffect(() => {
    if (categories.length === 0) return;
    const first = categories[0];

    if (selectedCategoryId == null || selectedCategoryId === '') {
      if (first) setSelectedCategoryId(first.id);
      return;
    }
    const categoryExists = categories.some(
      (c) => c.id === selectedCategoryId || String(c.id) === String(selectedCategoryId)
    );
    if (!categoryExists) {
      if (first) setSelectedCategoryId(first.id);
    }
  }, [categories, selectedCategoryId]);
  const [notes, setNotes] = useState('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmationDisplayOpen, setIsConfirmationDisplayOpen] = useState(false);
  const [showUberDoordashModal, setShowUberDoordashModal] = useState(false);
  const [uberDoordashDetails, setUberDoordashDetails] = useState<{ platform: 'Uber Eats' | 'DoorDash'; customerName: string } | null>(null);
  const [showCustomerNameModal, setShowCustomerNameModal] = useState(false);
  const [pendingOrderTypeForCustomerName, setPendingOrderTypeForCustomerName] = useState<'takeaway' | 'delivery' | null>(null);
  const [orderCustomerName, setOrderCustomerName] = useState<string | null>(null);
  /** GST-inclusive manual delivery fee (manual delivery orders only). */
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [selectedServingStaff, setSelectedServingStaff] = useState<StaffMember | null>(null);
  const [orderType, setOrderType] = useState<OrderType | null>(null);
  const [customerNumber, setCustomerNumber] = useState<number | undefined>(undefined);
  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [showTableCustomerModal, setShowTableCustomerModal] = useState(false);
  const [pendingTableSelection, setPendingTableSelection] = useState<Table | null>(null);
  const [showDineInCoversModal, setShowDineInCoversModal] = useState(false);
  const [pendingDineInTable, setPendingDineInTable] = useState<Table | null>(null);
  const [pendingDineInSpareSeats, setPendingDineInSpareSeats] = useState<number>(0);
  const [showBuffetTypeSelector, setShowBuffetTypeSelector] = useState(false);
  const [selectedBuffetType, setSelectedBuffetType] = useState<'breakfast' | 'lunch' | 'dinner' | null>(null);
  const [showBuffetPeopleModal, setShowBuffetPeopleModal] = useState(false);
  const [buffetNumberOfPeople, setBuffetNumberOfPeople] = useState<number>(1);
  const [appliedReward, setAppliedReward] = useState<LoyaltyReward | null>(null);
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [discountRequiresApproval, setDiscountRequiresApproval] = useState(false);
  const [discountApprovedBy, setDiscountApprovedBy] = useState<number | undefined>(undefined);
  const [discountApprovalReason, setDiscountApprovalReason] = useState<string | undefined>(undefined);
  const [discountApprovalTimestamp, setDiscountApprovalTimestamp] = useState<number | undefined>(undefined);
  const [discountMethod, setDiscountMethod] = useState<'manual' | 'first-table' | undefined>(undefined);
  const [firstTableDiscountAmount, setFirstTableDiscountAmount] = useState<number>(0);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponCampaignId, setCouponCampaignId] = useState<string | null>(null);
  const [couponDiscountType, setCouponDiscountType] = useState<'percentage' | 'fixed' | null>(null);
  const [couponDiscountValue, setCouponDiscountValue] = useState<number>(0);
  const [couponDiscountAmount, setCouponDiscountAmount] = useState<number>(0);
  const [couponUnitLabel, setCouponUnitLabel] = useState<string | undefined>(undefined);
  const [couponBuildingWing, setCouponBuildingWing] = useState<'hotel_wing' | 'tower_wing' | undefined>(undefined);
  const [couponError, setCouponError] = useState<string | null>(null);
  
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [pendingDiscount, setPendingDiscount] = useState<number>(0);
  const [pendingDiscountReason, setPendingDiscountReason] = useState<string | undefined>(undefined);
  const [pendingDiscountMethod, setPendingDiscountMethod] = useState<'manual' | 'first-table'>('manual');
  
  const [placedOrder, setPlacedOrder] = useState<Order | null>(null);
  const [orderToPrint, setOrderToPrint] = useState<Order | null>(null);
  const [printType, setPrintType] = useState<'customer' | 'kitchen'>('customer');
  const [printPendingOrder, setPrintPendingOrder] = useState<Order | null>(null);
  // Sub-bill receipt printing: either a closed SubBill (reprint) or a draft preview (before payment).
  const [subBillToPrint, setSubBillToPrint] = useState<{
    order: Order;
    subBill?: import('../types').SubBill;
    preview?: import('../components/pos/PrintableReceipt').SubBillPreview;
  } | null>(null);
  const [selectedHistoryOrder, setSelectedHistoryOrder] = useState<Order | null>(null);
  const [shouldAutoOpenPaymentModal, setShouldAutoOpenPaymentModal] = useState(false);
  const [isCompletingBuffet, setIsCompletingBuffet] = useState(false);
  const [uberIssueOrder, setUberIssueOrder] = useState<Order | null>(null);
  const [uberIssueSelectedItemIndices, setUberIssueSelectedItemIndices] = useState<number[]>([]);
  const [uberIssueReason, setUberIssueReason] = useState<'OUT_OF_STOCK' | 'ITEM_INCORRECT' | 'OTHER'>('OUT_OF_STOCK');
  const [uberIssueExplanation, setUberIssueExplanation] = useState('');
  const [isSubmittingUberIssue, setIsSubmittingUberIssue] = useState(false);
  const prevUberPendingCountRef = useRef<number>(uberPendingOrders.length);
  const enableUberIncomingAlertRef = useRef<boolean>(enableUberIncomingAlert);
  
  const playUberAlert = useCallback(() => {
    try {
      const audio = new Audio('/sounds/uber-chime.mp3');
      audio.volume = 1;
      // Fire and forget; browsers may block autoplay depending on user interaction.
      // Use .catch (not `void`) to swallow rejections like AbortError from interrupted play/pause.
      audio.play().catch(() => { /* autoplay restriction or interrupted */ });
    } catch {
      // Ignore audio errors (e.g. autoplay restrictions)
    }
  }, []);

  useEffect(() => {
    enableUberIncomingAlertRef.current = enableUberIncomingAlert;
  }, [enableUberIncomingAlert]);

  useEffect(() => {
    if (!enableUberIncomingAlertRef.current) return;
    if (uberPendingOrders.length > prevUberPendingCountRef.current) {
      playUberAlert();
    }
    prevUberPendingCountRef.current = uberPendingOrders.length;
  }, [uberPendingOrders.length, playUberAlert]);

  useEffect(() => {
    if (!enableUberIncomingAlertRef.current) return;
    if (uberPendingOrders.length === 0) return;
    const id = window.setInterval(() => {
      playUberAlert();
    }, 10000);
    return () => window.clearInterval(id);
  }, [uberPendingOrders.length, playUberAlert]);

  const handleSubmitUberIssue = useCallback(async () => {
    if (!uberIssueOrder) return;
    const uberOrderId = uberIssueOrder.deliveryInfo?.orderId;
    if (!uberOrderId) {
      alert('This order is missing the Uber order ID.');
      return;
    }
    if (!uberIssueSelectedItemIndices.length) {
      alert('Please select at least one item.');
      return;
    }
    const items = uberIssueSelectedItemIndices
      .map((idx) => uberIssueOrder.items[idx])
      .filter(Boolean)
      .map((it) => ({
        title: it.name,
        quantity: it.quantity,
        ...(it.cartItemId ? { cartItemId: it.cartItemId } : {}),
      }));
    if (!items.length) {
      alert('Please select at least one item.');
      return;
    }

    setIsSubmittingUberIssue(true);
    try {
      await reportUberOrderIssue(uberOrderId, {
        items,
        reasonCode: uberIssueReason,
        explanation: uberIssueExplanation.trim() || undefined,
      });
      alert('Issue reported to Uber. The customer will be asked to confirm changes.');
      setUberIssueOrder(null);
      setUberIssueSelectedItemIndices([]);
      setUberIssueExplanation('');
      setUberIssueReason('OUT_OF_STOCK');
    } catch (err) {
      console.error('reportUberOrderIssue failed:', err);
      alert((err as any)?.message || 'Failed to report issue to Uber.');
    } finally {
      setIsSubmittingUberIssue(false);
    }
  }, [uberIssueOrder, uberIssueSelectedItemIndices, uberIssueReason, uberIssueExplanation]);

  // History orders (Completed / Cancelled / Refunded) date filter — shared across those tabs
  const [historyOrdersDateFilter, setHistoryOrdersDateFilter] = useState<HistoryOrdersDateFilter>('today');
  const [completedOrderTypeFilter, setCompletedOrderTypeFilter] = useState<OrderType | 'all'>('all');
  const [customDateStart, setCustomDateStart] = useState<string>('');
  const [customDateEnd, setCustomDateEnd] = useState<string>('');
  const [historyOrdersFetched, setHistoryOrdersFetched] = useState<Order[]>([]);
  const [historyOrdersFetchLoading, setHistoryOrdersFetchLoading] = useState(false);
  const historyOrdersFetchKeyRef = useRef<string | null>(null);
  const [pendingOrdersFetched, setPendingOrdersFetched] = useState<Order[]>([]);
  const [pendingOrdersFetchLoading, setPendingOrdersFetchLoading] = useState(false);

  const businessTimeZone = integrationSettings?.businessHours?.timezone || 'Pacific/Auckland';

  const getCompletedOrdersWeekStartYmd = useCallback((): string => {
    const todayStr = ymdInTimeZone(businessTimeZone);
    const [year, month, day] = todayStr.split('-').map(Number);
    const nzDate = new Date(year, month - 1, day);
    const dayOfWeek = nzDate.getDay();
    const weekStart = new Date(nzDate);
    weekStart.setDate(nzDate.getDate() - dayOfWeek);
    return weekStart.toLocaleDateString('en-CA');
  }, [businessTimeZone]);
  
  // Determine available views - moved here before any conditional returns
  const hasActiveOrders = orders.filter(order => 
    order.paymentStatus === 'unpaid' &&
    !isUnpaidOnlineWebOrder(order) &&
    order.status !== 'cancelled' &&
    order.status !== 'refunded' &&
    new Date(order.createdAt).toDateString() === new Date().toDateString()
  ).length > 0;
  const defaultView = (hasActiveOrders && !orderType && !placedOrder) ? 'active-orders' : 'new-order';
  const initialPosView = getStoredPosView() ?? defaultView;
  const initialActiveView = getStoredActiveView() ?? 'floor';
  const [posView, setPosView] = useState<PosViewType>(initialPosView);
  const [activeView, setActiveView] = useState<ActiveViewType>(initialActiveView);

  useEffect(() => {
    const todayStr = ymdInTimeZone(businessTimeZone);
    const yesterdayStr = addDaysToYmd(todayStr, -1);

    let startYmd: string | null = null;
    let endYmd: string | null = null;
    let fetchKey: string | null = null;

    if (historyOrdersDateFilter === 'today') {
      startYmd = todayStr;
      endYmd = todayStr;
      fetchKey = `today:${todayStr}`;
    } else if (historyOrdersDateFilter === 'yesterday') {
      startYmd = yesterdayStr;
      endYmd = yesterdayStr;
      fetchKey = `yesterday:${yesterdayStr}`;
    } else if (historyOrdersDateFilter === 'weekly') {
      startYmd = getCompletedOrdersWeekStartYmd();
      endYmd = todayStr;
      fetchKey = `weekly:${startYmd}:${endYmd}`;
    } else if (historyOrdersDateFilter === 'custom' && customDateStart && customDateEnd) {
      startYmd = customDateStart;
      endYmd = customDateEnd;
      fetchKey = `custom:${customDateStart}:${customDateEnd}`;
    } else {
      setHistoryOrdersFetched([]);
      historyOrdersFetchKeyRef.current = null;
      setHistoryOrdersFetchLoading(false);
      return;
    }

    if (fetchKey === historyOrdersFetchKeyRef.current) return;

    let cancelled = false;
    setHistoryOrdersFetchLoading(true);

    void (async () => {
      try {
        const rows = await ordersService.getByDateRange(
          ymdToStartMs(startYmd!),
          ymdToEndMs(endYmd!),
        );
        if (cancelled) return;
        setHistoryOrdersFetched(rows);
        historyOrdersFetchKeyRef.current = fetchKey;
      } catch (err) {
        console.error('Failed to load history orders for range:', err);
        if (!cancelled) {
          setHistoryOrdersFetched([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryOrdersFetchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    historyOrdersDateFilter,
    customDateStart,
    customDateEnd,
    getCompletedOrdersWeekStartYmd,
    businessTimeZone,
  ]);

  useEffect(() => {
    if (posView !== 'pending' && posView !== 'floor') return;

    let cancelled = false;
    setPendingOrdersFetchLoading(true);

    void (async () => {
      try {
        const todayStr = ymdInTimeZone(businessTimeZone);
        const [unpaid, todayRows] = await Promise.all([
          ordersService.getOpenUnpaid(),
          ordersService.getByDateRange(ymdToStartMs(todayStr), ymdToEndMs(todayStr)),
        ]);
        if (cancelled) return;
        const map = new Map<string, Order>();
        for (const order of unpaid) map.set(order.id, order);
        for (const order of todayRows) map.set(order.id, order);
        setPendingOrdersFetched(Array.from(map.values()));
      } catch (err) {
        console.error('Failed to load pending orders:', err);
        if (!cancelled) setPendingOrdersFetched([]);
      } finally {
        if (!cancelled) setPendingOrdersFetchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [posView, businessTimeZone]);

  const printRef = useRef<HTMLDivElement>(null);

  const currentPlacedOrder = placedOrder
    ? orders.find((o) => o.id === placedOrder.id) ??
      pendingOrdersFetched.find((o) => o.id === placedOrder.id) ??
      null
    : null;
  // Prefer the live order from Firestore, but fall back to the locally-held copy when a live
  // snapshot arrives without meaningful items (avoids the sidebar briefly emptying). Always
  // returns an existing object reference — never a freshly-built object — so it is stable
  // across renders and cannot trigger update loops.
  const resolvedPlacedOrder: Order | null =
    placedOrder && currentPlacedOrder
      ? (!orderHasMeaningfulItems(currentPlacedOrder) && orderHasMeaningfulItems(placedOrder)
          ? placedOrder
          : currentPlacedOrder)
      : placedOrder || currentPlacedOrder || null;
  const orderFullySettled = resolvedPlacedOrder ? isPosOrderFullySettled(resolvedPlacedOrder) : false;
  const showOrderSummary = Boolean(
    placedOrder ||
    orderItems.length > 0 ||
    (posView === 'new-order' && orderType)
  );

  // Persist POS view state so background refresh or full-page reload doesn't reset the screen
  useEffect(() => {
    try {
      sessionStorage.setItem(POS_VIEW_KEY, posView);
      sessionStorage.setItem(POS_ACTIVE_VIEW_KEY, activeView);
    } catch (_) {}
  }, [posView, activeView]);

  // Update placedOrder when order is updated (e.g., after payment, kitchen completion, service).
  // Assign the live object reference directly so the next render sees `updatedOrder === placedOrder`
  // and the effect settles (never build a new object here or it re-fires forever).
  useEffect(() => {
    if (!placedOrder) return;
    const updatedOrder =
      orders.find((o) => o.id === placedOrder.id) ??
      pendingOrdersFetched.find((o) => o.id === placedOrder.id);
    if (!updatedOrder || updatedOrder === placedOrder) return;
    // Don't overwrite good local items with an empty/partial live snapshot.
    if (!orderHasMeaningfulItems(updatedOrder) && orderHasMeaningfulItems(placedOrder)) return;
    setPlacedOrder(updatedOrder);
  }, [orders, pendingOrdersFetched, placedOrder]);

  // Sync orderItems when resolvedPlacedOrder updates (kitchen/service changes) - only when data actually changed
  const orderSyncKeyRef = useRef<string>('');
  useEffect(() => {
    if (!resolvedPlacedOrder || !placedOrder || resolvedPlacedOrder.id !== placedOrder.id) return;
    const liveItems = resolvedPlacedOrder.items ?? [];
    const liveKey = liveItems.map(i => `${i.lineId}|${i.quantity}|${i.quantityCompleted ?? ''}|${i.quantityServed ?? ''}|${i.status}|${i.served}|${i.removed}`).join(';');
    if (orderSyncKeyRef.current === liveKey) return;
    orderSyncKeyRef.current = liveKey;
    setOrderItems(prev => {
      if (prev.length !== liveItems.length) return liveItems;
      const prevKey = prev.map(p => `${p.lineId}|${p.quantity}|${p.quantityCompleted ?? ''}|${p.quantityServed ?? ''}|${p.status}|${p.served}|${p.removed}`).join(';');
      return prevKey === liveKey ? prev : liveItems;
    });
  }, [resolvedPlacedOrder?.id, resolvedPlacedOrder?.items, placedOrder?.id]);

  // Reset sync key when switching to a different order
  useEffect(() => {
    if (!placedOrder) orderSyncKeyRef.current = '';
  }, [placedOrder?.id]);

  useEffect(() => {
    if (shouldAutoOpenPaymentModal && resolvedPlacedOrder) {
      setIsPaymentModalOpen(true);
      setShouldAutoOpenPaymentModal(false);
    }
  }, [shouldAutoOpenPaymentModal, resolvedPlacedOrder]);

  // Helper: use payment timestamp (when sale actually occurred) for “today” calculations
  const getOrderSaleTimestamp = useCallback((order: Order): number => orderSaleAtMs(order), []);

  const todayKey = useMemo(() => ymdInTimeZone(businessTimeZone), [businessTimeZone]);
  const yesterdayKey = useMemo(() => addDaysToYmd(todayKey, -1), [todayKey]);

  const getOrderSaleDateKey = useCallback(
    (order: Order): string => orderSaleDateKey(order, businessTimeZone),
    [businessTimeZone],
  );

  const pendingOrdersSource = useMemo(() => {
    const map = new Map<string, Order>();
    for (const order of pendingOrdersFetched) map.set(order.id, order);
    for (const order of orders) map.set(order.id, order);
    return Array.from(map.values());
  }, [pendingOrdersFetched, orders]);

  /** Event stall ticket # (same algorithm as Mobile POS). */
  const completedEventTokenLabelById = useMemo(
    () =>
      buildEventTokenLabelById(
        orders,
        integrationSettings?.eventStall?.tokenCounterResetAt,
        integrationSettings?.eventStall?.eventTokenRollMax,
      ),
    [orders, integrationSettings?.eventStall?.tokenCounterResetAt, integrationSettings?.eventStall?.eventTokenRollMax],
  );

  // Today’s orders (for active orders list, register close, and End of Day checklist) –
  // match by trading day in business timezone, not raw timestamp.
  const todaysOrders = useMemo(() => {
    return orders.filter((o) => {
      return (
        getOrderSaleDateKey(o) === todayKey &&
        o.status !== 'cancelled' &&
        o.status !== 'refunded' &&
        !o.isDeleted &&
        orderHasMeaningfulItems(o) &&
        !isUnpaidOnlineWebOrder(o)
      );
    });
  }, [orders, getOrderSaleDateKey, todayKey]);
  const historyOrdersSource = useMemo(() => {
    const map = new Map<string, Order>();
    for (const order of historyOrdersFetched) map.set(order.id, order);
    for (const order of orders) map.set(order.id, order);
    return Array.from(map.values());
  }, [historyOrdersFetched, orders]);

  const historyDateFilterOpts = useMemo(
    () => ({
      todayKey,
      yesterdayKey,
      weekStartYmd: getCompletedOrdersWeekStartYmd(),
      customDateStart,
      customDateEnd,
      getOrderDateKey: getOrderSaleDateKey,
    }),
    [
      todayKey,
      yesterdayKey,
      getCompletedOrdersWeekStartYmd,
      customDateStart,
      customDateEnd,
      getOrderSaleDateKey,
    ],
  );

  const cancelledTabBadgeCount = useMemo(
    () =>
      filterOrdersByHistoryDate(
        historyOrdersSource.filter(
          (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'cancelled',
        ),
        historyOrdersDateFilter,
        historyDateFilterOpts,
      ).length,
    [historyOrdersSource, historyOrdersDateFilter, historyDateFilterOpts],
  );
  const refundedTabBadgeCount = useMemo(
    () =>
      filterOrdersByHistoryDate(
        historyOrdersSource.filter(
          (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'refunded',
        ),
        historyOrdersDateFilter,
        historyDateFilterOpts,
      ).length,
    [historyOrdersSource, historyOrdersDateFilter, historyDateFilterOpts],
  );

  // Orders that belong to this register session (for close modal sales and EOD).
  // Orders that belong to this register session (for close modal sales and EOD).
  // Included if order.sessionId matches this session OR order timestamp is within session start/end.
  // Orders without sessionId are only included when in the time window; otherwise they can appear "missing" from this register.
  const sessionOrders = useMemo(() => {
    if (!registerSession) return [];
    const sessionStart = registerSession.startTime;
    const sessionEnd = registerSession.endTime ?? Date.now();
    return orders.filter((order) => {
      if (isSampleOrDemoOrderId(order.id)) return false;
      if (order.isDeleted) return false;
      if (order.sessionId === registerSession.id) return true;
      if (
        !order.sessionId &&
        orderCreatedAtMs(order) >= sessionStart &&
        orderCreatedAtMs(order) <= sessionEnd &&
        orderHasMeaningfulItems(order)
      ) {
        return true;
      }
      return false;
    });
  }, [orders, registerSession]);

  useEffect(() => {
    try {
      if(placedOrder) return;
      const savedDraft = localStorage.getItem(DRAFT_ORDER_KEY);
      if (savedDraft) {
        const { items, notes, customer, table, type, reward, deliveryCharge: savedDelivery } = JSON.parse(savedDraft);
        if (Array.isArray(items)) setOrderItems(items.map((item: OrderItem) => ({...item, status: 'pending'})));
        if (notes) setNotes(notes);
        if (customer) setSelectedCustomer(customer);
        if (table) {
          setSelectedTable(table);
          if (table) setActiveView('menu');
        }
        if (type) setOrderType(type);
        if (reward) setAppliedReward(reward);
        if (typeof savedDelivery === 'number' && !Number.isNaN(savedDelivery) && savedDelivery >= 0) {
          setDeliveryCharge(savedDelivery);
        }
      }
    } catch (error) {
      console.error("Failed to load draft order:", error);
    }
  }, [placedOrder]);

  useEffect(() => {
    if (placedOrder) return;
    const draft = {
      items: orderItems,
      notes,
      customer: selectedCustomer,
      table: selectedTable,
      type: orderType,
      reward: appliedReward,
      deliveryCharge: orderType === 'delivery' ? deliveryCharge : 0,
    };
    localStorage.setItem(DRAFT_ORDER_KEY, JSON.stringify(draft));
  }, [orderItems, notes, selectedCustomer, selectedTable, orderType, appliedReward, placedOrder, deliveryCharge]);

  useEffect(() => {
    if (orderToPrint && printRef.current) {
        printRef.current.style.display = 'block';
        // Delay so embedded content (e.g. QR code) has time to generate before print
        const printDelay = printType === 'customer' ? 350 : 100;
        const timer = setTimeout(() => {
            window.print();
            setTimeout(() => {
                if (printRef.current) {
                    printRef.current.style.display = 'none';
                }
                setOrderToPrint(null);
            }, 100);
        }, printDelay);
        return () => clearTimeout(timer);
    }
  }, [orderToPrint, printType]);

  // Mirror of the orderToPrint effect for sub-bill prints (reprint or preview before payment).
  useEffect(() => {
    if (subBillToPrint && printRef.current) {
      printRef.current.style.display = 'block';
      const timer = setTimeout(() => {
        window.print();
        setTimeout(() => {
          if (printRef.current) printRef.current.style.display = 'none';
          setSubBillToPrint(null);
        }, 100);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [subBillToPrint]);

  const clearOrder = (clearTable = true, resetOrderType = true) => {
    setOrderItems([]);
    setNotes('');
    setSelectedCustomer(null);
    if(clearTable) setSelectedTable(null);
    setSelectedServingStaff(null);
    if (resetOrderType) {
      setOrderType(null);
      setUberDoordashDetails(null);
      setOrderCustomerName(null);
      setSelectedBuffetType(null);
      setBuffetNumberOfPeople(1);
    }
    setAppliedReward(null);
    setManualDiscount(0);
    setDiscountRequiresApproval(false);
    setDiscountApprovedBy(undefined);
    setDiscountApprovalReason(undefined);
    setDiscountApprovalTimestamp(undefined);
    setCustomerNumber(undefined);
    setCustomerName(undefined);
    setDeliveryCharge(0);
    localStorage.removeItem(DRAFT_ORDER_KEY);
    setIsPlacingOrder(false);
  };
  
  const handleNewOrder = () => {
    clearOrder();
    setPlacedOrder(null);
    setOrderType(null);
    setActiveView('floor');
  };

  const handleSelectOrderType = (type: OrderType) => {
    if (type === 'uber-doordash') {
      setShowUberDoordashModal(true);
      return;
    }
    if (type === 'takeaway' || type === 'delivery') {
      setPendingOrderTypeForCustomerName(type);
      setShowCustomerNameModal(true);
      return;
    }
    setPlacedOrder(null);
    setUberDoordashDetails(null);
    setOrderCustomerName(null);
    setOrderType(type);
    if (type === 'buffet') {
      setShowBuffetTypeSelector(true);
    } else if (type === 'dine-in') {
      setPosView('floor');
      setActiveView('floor');
    } else {
      setActiveView('menu');
    }
  };

  const handleCustomerNameModalSubmit = (name: string) => {
    setOrderCustomerName(name || null);
    // Starting a fresh takeaway/delivery order — ensure any previously loaded order is cleared.
    setPlacedOrder(null);
    if (pendingOrderTypeForCustomerName) {
      setOrderType(pendingOrderTypeForCustomerName);
      if (pendingOrderTypeForCustomerName === 'delivery') {
        setDeliveryCharge(0);
      }
      setPendingOrderTypeForCustomerName(null);
    }
    setShowCustomerNameModal(false);
    setActiveView('menu');
  };

  const handleSelectBuffetType = (type: 'breakfast' | 'lunch' | 'dinner') => {
    setSelectedBuffetType(type);
    setShowBuffetTypeSelector(false);
    // For buffet, need to select table first
    setPosView('floor');
    setActiveView('floor');
  };

  const handleConfirmBuffetPeople = async (numberOfPeople: number) => {
    if (!selectedTable || !selectedBuffetType) {
      alert('Please select a table first.');
      return;
    }

    setBuffetNumberOfPeople(numberOfPeople);
    setShowBuffetPeopleModal(false);
    
    // Calculate price per person and total
    const buffetPrice = integrationSettings?.buffetPricing?.[selectedBuffetType] ?? 0;
    if (buffetPrice <= 0) {
      alert(`Please configure a price for the ${selectedBuffetType} buffet before taking this order (Settings → Buffet Pricing).`);
      return;
    }
    const totalAmount = buffetPrice * numberOfPeople;
    
    // Create a temporary summary order for payment
    // After payment, we'll create individual orders for each person
    const buffetStartTime = Date.now();
    const buffetEndTime = buffetStartTime + (60 * 60 * 1000); // 1 hour
    
    const buffetItemId = -Math.floor(Math.random() * 1000000) - 1;
    const buffetItem: OrderItem = {
      id: buffetItemId,
      name: `${selectedBuffetType.charAt(0).toUpperCase() + selectedBuffetType.slice(1)} Buffet`,
      categoryId: 0,
      price: buffetPrice,
      quantity: numberOfPeople,
      imageUrl: '',
      status: 'pending',
      served: false,
    };

    const summaryOrder = await onPlaceOrder([buffetItem], 'buffet', {
      tableId: selectedTable.id,
      customerId: selectedCustomer?.id,
      customerNumber: undefined, // Will be set per person later
      customerName: selectedCustomer?.name || `Buffet - ${numberOfPeople} ${numberOfPeople === 1 ? 'person' : 'people'}`,
      notes: `${selectedBuffetType.charAt(0).toUpperCase() + selectedBuffetType.slice(1)} Buffet - ${numberOfPeople} ${numberOfPeople === 1 ? 'person' : 'people'}`,
      appliedReward: undefined,
      manualDiscount: 0,
      discountRequiresApproval: false,
      buffetType: selectedBuffetType,
      buffetStartTime: buffetStartTime,
      buffetEndTime: buffetEndTime
    });
    
    if (summaryOrder) {
      // Update order with correct total and status
      const updatedOrder: Order = {
        ...summaryOrder,
        totalAmount: totalAmount, // Total for all people
        status: 'buffet' as OrderStatus,
      };
      
      await onUpdateOrder(updatedOrder);
      setPlacedOrder(updatedOrder);
      localStorage.removeItem(DRAFT_ORDER_KEY);
      setShouldAutoOpenPaymentModal(true);
    }
  };

  const handleOpenBuffetPeopleModal = () => {
    if (!selectedTable) {
      alert('Please select a table for this buffet order first.');
      setPosView('floor');
      setActiveView('floor');
      return;
    }
    setShowBuffetPeopleModal(true);
  };

  const handleCompleteBuffetOrder = async () => {
    if (!resolvedPlacedOrder || resolvedPlacedOrder.orderType !== 'buffet' || resolvedPlacedOrder.status !== 'buffet') return;
    await handleEndBuffetSessionForOrder(resolvedPlacedOrder);
  };

  /** End buffet session (complete early or on time) for any buffet order. */
  const handleEndBuffetSessionForOrder = async (order: Order) => {
    if (order.orderType !== 'buffet' || order.status !== 'buffet') return;
    try {
      setIsCompletingBuffet(true);
      const now = Date.now();
      const updatedOrder: Order = {
        ...order,
        status: 'served',
        buffetEndTime: now,
        servedAt: now,
      };
      await onUpdateOrder(updatedOrder);
      if (resolvedPlacedOrder?.id === order.id) setPlacedOrder(updatedOrder);
      setSelectedHistoryOrder(null);
      alert('Buffet session ended.');
    } catch (error) {
      console.error('Failed to end buffet session:', error);
      alert('Failed to end buffet session. Please try again.');
    } finally {
      setIsCompletingBuffet(false);
    }
  };

  // Create individual orders for each person after payment
  const handleCreateBuffetPersonOrders = async () => {
    if (!resolvedPlacedOrder || !selectedTable || !selectedBuffetType || !buffetNumberOfPeople) return;
    
    const summaryOrder = resolvedPlacedOrder;
    const pricePerPerson = integrationSettings?.buffetPricing?.[selectedBuffetType] || 0;
    const totalPaid = summaryOrder.payments.reduce((sum, p) => sum + p.amount, 0);
    
    // Create a separate order for each person
    for (let i = 1; i <= buffetNumberOfPeople; i++) {
      const buffetStartTime = Date.now();
      const buffetEndTime = buffetStartTime + (60 * 60 * 1000); // 1 hour per person
      
      // Create order with empty items array - buffet doesn't need items
      const buffetItemId = -Math.floor(Math.random() * 1000000) - 1;
      const buffetItem: OrderItem = {
        id: buffetItemId,
        name: `${selectedBuffetType.charAt(0).toUpperCase() + selectedBuffetType.slice(1)} Buffet`,
        categoryId: 0,
        price: pricePerPerson,
        quantity: 1,
        imageUrl: '',
        status: 'pending',
        served: false,
      };

      const servingStaff = selectedServingStaff ?? currentUser;
      const newOrder = await onPlaceOrder([buffetItem], 'buffet', {
        tableId: selectedTable.id,
        customerId: selectedCustomer?.id,
        customerNumber: i, // Each person gets a customer number (1, 2, 3, etc.)
        customerName: selectedCustomer?.name ? `${selectedCustomer.name} - Person ${i}` : `Person ${i}`,
        notes: `${selectedBuffetType.charAt(0).toUpperCase() + selectedBuffetType.slice(1)} Buffet - Person ${i}`,
        appliedReward: undefined,
        manualDiscount: 0,
        discountRequiresApproval: false,
        buffetType: selectedBuffetType,
        buffetStartTime: buffetStartTime,
        buffetEndTime: buffetEndTime,
        ...(servingStaff && { servingStaffId: servingStaff.id, servingStaffName: servingStaff.name }),
      });
      
      if (newOrder) {
        // Split payments from summary order among individual orders
        const splitPayments: Payment[] = summaryOrder.payments.map((p) => ({
          ...p,
          amount: Math.round((p.amount / buffetNumberOfPeople) * 100) / 100,
        }));
        
        // Update order with correct total (price per person) and status
        const updatedOrder: Order = {
          ...newOrder,
          totalAmount: pricePerPerson, // Each person pays the per-person price
          status: 'buffet' as OrderStatus,
          paymentStatus: 'paid', // Mark as paid since payment was already processed
          payments: splitPayments,
        };
        
        await onUpdateOrder(updatedOrder);
      }
    }
  };

  const handleCancelBuffetType = () => {
    setShowBuffetTypeSelector(false);
    setOrderType(null);
    setSelectedBuffetType(null);
  };

  const handleSelectTableFromFloor = (tableId: number) => {
      // Don't handle table clicks in merge mode - that's handled by handleTableSelect
      if (isMergeMode) return;
      
      const table = tables.find(t => t.id === tableId);
      if (!table) return;

      // Check if it's a merged table - if so, use the primary merged table for ordering
      let tableToUse = table;
      if (table.mergedWith && table.mergedWith.length > 1) {
        const primaryId = table.mergedWith[0];
        const isPrimaryMerged = primaryId != null && Number(primaryId) === Number(table.id);
        if (!isPrimaryMerged) {
          // If clicking a secondary merged table, find the primary one
          const primaryTable = tables.find(t => Number(t.id) === Number(primaryId));
          if (primaryTable) {
            tableToUse = primaryTable;
          } else {
            // If primary table not found, just use the clicked table
            tableToUse = table;
          }
        }
      }

      const activeOrders = pendingOrdersSource.filter((o) =>
        isFloorPlanActiveTableOrder(o, tableToUse.id, businessTimeZone) ||
        isFloorPlanActiveTableOrder(o, tableId, businessTimeZone),
      );
      const hasActiveOrders = activeOrders.length > 0;
      const seatsUsed = getTableSeatsUsed(activeOrders);
      const spareSeats = Math.max(0, tableToUse.capacity - seatsUsed);

      if (tableToUse.status === 'available' && !hasActiveOrders) {
          // Table is available - start new order (clear any previously-loaded order)
          const keepOrderType = orderType === 'buffet';
          clearOrder(true, !keepOrderType);
          setPlacedOrder(null);
          setSelectedTable(tableToUse);
          setSelectedServingStaff(currentUser ?? null);
          
          // If buffet order type is selected, open cover-count modal (no à la carte menu)
          if (orderType === 'buffet' && selectedBuffetType) {
            setShowBuffetPeopleModal(true);
            return;
          }
          
          // For regular dine-in orders
          setOrderType('dine-in');
          setCustomerNumber(1); // Default to customer 1 for new table
          setCustomerName(undefined);
          setPosView('new-order');
          setActiveView('menu');
      } else {
          if (hasActiveOrders) {
            // Show customer selection when: multiple orders, or single order but table has spare capacity (so we can seat new party)
            if (activeOrders.length > 1 || spareSeats > 0) {
              setPendingTableSelection(tableToUse);
              setShowTableCustomerModal(true);
            } else {
              // Single order and no spare seats - load it directly
              const activeOrder = activeOrders[0];
              setPlacedOrder(activeOrder);
              setOrderItems(activeOrder.items);
              setNotes(activeOrder.notes || '');
              setSelectedTable(tableToUse);
              setCustomerNumber(activeOrder.customerNumber);
              setCustomerName(activeOrder.customerName);
              const customer = customers.find(c => c.id === activeOrder.customerId);
              setSelectedCustomer(customer || null);
              setAppliedReward(activeOrder.appliedReward || null);
              setOrderType(activeOrder.orderType);
              const servingStaff = activeOrder.servingStaffId != null ? staffMembers.find(s => s.id === activeOrder.servingStaffId) ?? null : null;
              setSelectedServingStaff(servingStaff);
              setPosView('new-order');
              setActiveView('menu');
            }
          } else {
             // Table marked occupied but no active orders - start new order
             const keepBuffetType = orderType === 'buffet';
             clearOrder(true, !keepBuffetType);
             setSelectedTable(tableToUse);
             setSelectedServingStaff(currentUser ?? null);
             
             // If buffet order type is selected, open cover-count modal
             if (orderType === 'buffet' && selectedBuffetType) {
               setShowBuffetPeopleModal(true);
               return;
             }
             
             // For regular dine-in orders
             setOrderType('dine-in');
             // Determine next customer number
             const existingOrders = orders.filter(o => {
               if (o.tableId !== tableId && o.tableId !== tableToUse.id) return false;
               if (o.status === 'cancelled' || o.status === 'refunded') return false;
               const orderDateKey = new Date(o.timestamp).toDateString();
               if (orderDateKey !== todayKey) return false;
               return o.customerNumber !== undefined;
             });
             const nextCustomerNumber = existingOrders.length > 0
               ? Math.max(...existingOrders.map(o => o.customerNumber!)) + 1
               : 1;
             setCustomerNumber(nextCustomerNumber);
             setCustomerName(undefined);
             setPosView('new-order');
             setActiveView('menu');
          }
      }
  };

  const handleSelectPendingOrder = (order: Order) => {
    const liveOrder = pendingOrdersSource.find((o) => o.id === order.id) ?? order;
    // Load the existing order for editing/payment
    setPlacedOrder(liveOrder);
    setOrderItems(liveOrder.items);
    setNotes(liveOrder.notes || '');
    setOrderType(liveOrder.orderType);
    
    // Load customer if exists
    if (liveOrder.customerId) {
      const customer = customers.find(c => c.id === liveOrder.customerId);
      setSelectedCustomer(customer || null);
    } else {
      setSelectedCustomer(null);
    }
    
    // Load table if dine-in
    if ((liveOrder.orderType === 'dine-in' || liveOrder.orderType === 'buffet') && liveOrder.tableId) {
      const table = tables.find(t => t.id === liveOrder.tableId);
      setSelectedTable(table || null);
    } else {
      setSelectedTable(null);
    }
    
    // Load serving staff if set on order
    const servingStaff = liveOrder.servingStaffId != null ? staffMembers.find(s => s.id === liveOrder.servingStaffId) ?? null : null;
    setSelectedServingStaff(servingStaff);
    
    // Load applied reward if exists
    setAppliedReward(liveOrder.appliedReward || null);
    
    // Load discount info if exists
    setManualDiscount(liveOrder.manualDiscount || 0);
    setDiscountRequiresApproval(liveOrder.discountRequiresApproval || false);
    setDiscountApprovedBy(liveOrder.discountApprovedBy);
    setDiscountApprovalReason(liveOrder.discountApprovalReason);
    setDiscountApprovalTimestamp(liveOrder.discountApprovalTimestamp);
    
    // Open the full POS ordering screen so staff can add items or pay.
    setPosView('new-order');
    setActiveView('menu');
  };

  const [meatOptionModalItem, setMeatOptionModalItem] = useState<MenuItemType | null>(null);
  
  // Table merge/split/transfer state
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
  const [selectedTableForSplit, setSelectedTableForSplit] = useState<Table | null>(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [tableToSplit, setTableToSplit] = useState<Table | null>(null);
  const [orderToTransfer, setOrderToTransfer] = useState<{ order: Order; table: Table } | null>(null);

  /** In merge mode: single selected table that is the primary of a merged group (split target). */
  const mergedPrimarySelectedInMergeMode = useMemo(() => {
    if (!isMergeMode || selectedTableIds.length !== 1) return null;
    const id = selectedTableIds[0];
    const t = tables.find(x => Number(x.id) === Number(id));
    if (!t?.mergedWith || t.mergedWith.length < 2) return null;
    const primaryId = t.mergedWith[0];
    if (Number(primaryId) !== Number(t.id)) return null;
    return t;
  }, [isMergeMode, selectedTableIds, tables]);
  
  // Override, remake, and fire mode state
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showRemakeModal, setShowRemakeModal] = useState(false);
  const [showFireModeModal, setShowFireModeModal] = useState(false);
  const [showCancelApprovalModal, setShowCancelApprovalModal] = useState(false);
  const [pendingCancelOrder, setPendingCancelOrder] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [orderToOverride, setOrderToOverride] = useState<Order | null>(null);
  const [orderToRemake, setOrderToRemake] = useState<Order | null>(null);
  const [showBuffetExtensionModal, setShowBuffetExtensionModal] = useState(false);
  const [orderToExtend, setOrderToExtend] = useState<Order | null>(null);

  const handleAddItemToOrder = (itemToAdd: MenuItemType, selectedMeatOption?: string) => {
    // Only prevent adding items if order is paid - allow adding even if some items are served
    if(orderFullySettled) return;
    
    // If item has meat options and no option was selected, show modal
    if (itemToAdd.meatOptions && itemToAdd.meatOptions.length > 0 && !selectedMeatOption) {
      setMeatOptionModalItem(itemToAdd);
      return;
    }
    
    // Only match non-removed lines so we can add the same item again after removing it
    const existingItem = orderItems.find(item => 
      !item.removed &&
      item.id === itemToAdd.id && 
      (!itemToAdd.meatOptions || item.selectedMeatOption === selectedMeatOption)
    );
    const quantityToAdd = existingItem ? existingItem.quantity + 1 : 1;
    
    // Check daily availability using reserved quantity calculation
    if (itemToAdd.hasDailyAvailability) {
      const availableQuantity = getAvailableQuantity(itemToAdd, orders);
      if (availableQuantity <= 0) {
        alert('This item is currently finished for today.');
        return;
      }
      if (quantityToAdd > availableQuantity) {
        alert(`Only ${availableQuantity} plate(s) available. Cannot add ${quantityToAdd} to order.`);
        return;
      }
    }
    
    // Do NOT update menu item database when adding to cart - reservation happens when order is placed
    setOrderItems(prevItems => {
      const existingItem = prevItems.find(item => 
        !item.removed &&
        item.id === itemToAdd.id && 
        (!itemToAdd.meatOptions || item.selectedMeatOption === selectedMeatOption)
      );
      if (existingItem) {
        return prevItems.map(item =>
          !item.removed &&
          item.id === itemToAdd.id && (!itemToAdd.meatOptions || item.selectedMeatOption === selectedMeatOption)
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      }
      return [...prevItems, { 
        ...itemToAdd, 
        quantity: 1, 
        status: 'pending',
        selectedMeatOption: selectedMeatOption,
      }];
    });
  };

  const handleMeatOptionSelected = (item: MenuItemType, selectedOption: string) => {
    setMeatOptionModalItem(null);
    handleAddItemToOrder(item, selectedOption);
  };
  
  const handleAddComboToOrder = (combo: QuickAddItem | QuickAddCombo) => {
    // Only prevent adding items if order is paid - allow adding even if some items are served
    if(orderFullySettled) return;
    
    setOrderItems(prevItems => {
      const newItems = [...prevItems];
      
      // Always add individual items so they show in kitchen/service screens
      let individualItemsTotal = 0;
      
      combo.items.forEach(comboItem => {
        const menuItem = menuItems.find(mi => mi.id === comboItem.menuItemId);
        if (!menuItem) return;
        
        const existingItemIndex = newItems.findIndex(item => !item.removed && item.id === comboItem.menuItemId);
        if (existingItemIndex > -1) {
          newItems[existingItemIndex] = { 
            ...newItems[existingItemIndex], 
            quantity: newItems[existingItemIndex].quantity + comboItem.quantity 
          };
          individualItemsTotal += menuItem.price * comboItem.quantity;
        } else {
          newItems.push({ ...menuItem, quantity: comboItem.quantity, status: 'pending' });
          individualItemsTotal += menuItem.price * comboItem.quantity;
        }
      });
      
      // If combo has a special price, add a discount item to adjust the total
      const comboPrice = (combo as QuickAddCombo).price;
      const numericPrice = typeof comboPrice === 'string' ? parseFloat(comboPrice) : comboPrice;
      const hasComboPrice = numericPrice !== undefined && numericPrice !== null && !isNaN(numericPrice) && numericPrice > 0;
      
      if (hasComboPrice && numericPrice < individualItemsTotal) {
        // Calculate discount amount
        const discountAmount = individualItemsTotal - numericPrice;
        
        // Add a discount item (negative price to reduce total)
        // Note: Discount items are automatically marked as completed when all preparable items are done
        const discountItem: OrderItem = {
          id: `combo-discount-${combo.id}-${Date.now()}`,
          name: `${combo.name} - Combo Discount`,
          price: -discountAmount, // Negative price for discount
          categoryId: 0,
          imageUrl: '',
          quantity: 1,
          status: 'pending', // Status will be auto-updated when all preparable items are completed
        };
        newItems.push(discountItem);
      }
      
      return newItems;
    });
  };

  const handleUpdateQuantity = (itemId: number, newQuantity: number) => {
    const idx = orderItems.findIndex(i => i.id === itemId);
    if (idx < 0) return;
    if (newQuantity <= 0) {
      handleRemoveItem(idx);
      return;
    }
    const item = orderItems[idx];
    const minCompleted = item.quantityCompleted ?? (item.status === 'completed' ? item.quantity : 0);
    if (minCompleted > 0 && newQuantity < minCompleted) return; // Can't reduce below kitchen-completed
    setOrderItems(prevItems =>
      prevItems.map((i, iidx) => iidx === idx ? { ...i, quantity: newQuantity } : i)
    );
  };

  const handleRemoveItem = (index: number) => {
    setOrderItems(prevItems => prevItems.map((item, i) => i === index ? { ...item, removed: true } : item));
  };

  const handleUpdateItemNote = (itemId: number, note: string) => {
    setOrderItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, notes: note || undefined } : item
      )
    );
  };
  
  const handleSelectCustomer = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setIsCustomerModalOpen(false);
  };
  
  const handleSelectReward = (reward: LoyaltyReward) => {
      setAppliedReward(reward);
      setIsRewardModalOpen(false);
  };

  const handleApplyDiscount = async (discount: number, reason: string | undefined, method: 'manual' | 'first-table') => {
    const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
    const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
    const subtotal = totalBeforeDiscount - rewardDiscount;
    const discountPercentage = subtotal > 0 ? (discount / subtotal) * 100 : 0;
    
    // Manager approval threshold applies only to manual discounts, not First Table.
    if (method === 'manual' && discountPercentage > 20) {
      // Requires manager approval
      setPendingDiscount(discount);
      setPendingDiscountReason(reason);
      setPendingDiscountMethod(method);
      setIsApprovalModalOpen(true);
    } else {
      // No approval needed
      setManualDiscount(discount);
      setDiscountMethod(method);
      setFirstTableDiscountAmount(method === 'first-table' ? discount : 0);
      setDiscountRequiresApproval(false);
      setDiscountApprovalReason(reason);
      
      // If this is an existing order, update it immediately
      if (placedOrder && currentPlacedOrder) {
        const updatedOrder: Order = {
          ...currentPlacedOrder,
          manualDiscount: method === 'manual' ? discount : 0,
          discountMethod: method,
          firstTableDiscountAmount: method === 'first-table' ? discount : 0,
          discountRequiresApproval: false,
          discountApprovalReason: reason,
        };
        await onUpdateOrder(updatedOrder);
      }
    }
  };

  const handleApproveDiscount = async (staffId: number, reason: string) => {
    const finalReason = reason || pendingDiscountReason;
    setManualDiscount(pendingDiscount);
    setDiscountMethod(pendingDiscountMethod);
    setFirstTableDiscountAmount(pendingDiscountMethod === 'first-table' ? pendingDiscount : 0);
    setDiscountRequiresApproval(true);
    setDiscountApprovedBy(staffId);
    setDiscountApprovalReason(finalReason);
    setDiscountApprovalTimestamp(Date.now());
    
    // If this is an existing order, update it immediately
    if (placedOrder && currentPlacedOrder) {
        const updatedOrder: Order = {
          ...currentPlacedOrder,
          manualDiscount: pendingDiscountMethod === 'manual' ? pendingDiscount : 0,
          discountMethod: pendingDiscountMethod,
          firstTableDiscountAmount: pendingDiscountMethod === 'first-table' ? pendingDiscount : 0,
          discountRequiresApproval: true,
          discountApprovedBy: staffId,
          discountApprovalReason: finalReason,
          discountApprovalTimestamp: Date.now(),
        };
      await onUpdateOrder(updatedOrder);
    }
    
    setPendingDiscount(0);
    setPendingDiscountReason(undefined);
    setPendingDiscountMethod('manual');
  };

  const handleRemoveDiscount = () => {
    setManualDiscount(0);
    setDiscountMethod(undefined);
    setFirstTableDiscountAmount(0);
    setDiscountRequiresApproval(false);
    setDiscountApprovedBy(undefined);
    setDiscountApprovalReason(undefined);
    setDiscountApprovalTimestamp(undefined);
    setCouponCode(null);
    setCouponCampaignId(null);
    setCouponDiscountType(null);
    setCouponDiscountValue(0);
    setCouponDiscountAmount(0);
    setCouponUnitLabel(undefined);
    setCouponBuildingWing(undefined);
    setCouponError(null);
  };

  const handleApplyCoupon = async (code: string) => {
    const normalizedCode = code.trim().toUpperCase().replace(/^CPN-/, '').replace(/^UNIT-/, '');
    try {
      if (discountMethod === 'first-table') {
        throw new Error('Remove First Table discount before applying a code.');
      }
      const coupon = await couponsService.getByCode(normalizedCode);
      const now = Date.now();
      if (!coupon) throw new Error('Coupon not found');
      const campaign = coupon.campaignId ? await couponCampaignsService.getById(coupon.campaignId) : null;
      if (campaign && campaign.status && campaign.status !== 'published') {
        throw new Error(`Campaign is ${campaign.status}. Coupon not available in POS yet`);
      }
      const validation = await couponsService.isValidForRedemption(coupon, now);
      if (!validation.ok) throw new Error(validation.reason || 'Coupon is not valid');
      if (coupon.appliesTo !== 'all') {
        const eligible = orderItems.some((item) => !item.removed && (coupon.appliesTo as number[]).includes(Number(item.id)));
        if (!eligible) throw new Error('Coupon does not apply to these items');
      }
      const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
      const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
      const base = Math.max(0, totalBeforeDiscount - rewardDiscount);
      const discount = coupon.discountType === 'percentage'
        ? Math.min(base, (base * coupon.discountValue) / 100)
        : Math.min(base, coupon.discountValue);

      setManualDiscount(discount);
      setDiscountMethod('manual');
      setCouponCode(coupon.code);
      setCouponCampaignId(coupon.campaignId);
      setCouponDiscountType(coupon.discountType);
      setCouponDiscountValue(coupon.discountValue);
      setCouponDiscountAmount(discount);
      setCouponUnitLabel(coupon.unitLabel ?? undefined);
      setCouponBuildingWing(
        coupon.buildingWing ?? campaign?.buildingWing ?? undefined
      );
      setCouponError(null);
      await couponUsageLogsService.create({
        couponCode: coupon.code,
        couponId: coupon.id,
        campaignId: coupon.campaignId,
        staffId: currentUser?.id,
        attemptedAt: now,
        result: 'applied',
      });
      await couponRedemptionsService.create({
        couponCode: coupon.code,
        couponId: coupon.id,
        campaignId: coupon.campaignId,
        staffId: currentUser?.id,
        redeemedAt: now,
        status: 'applied',
      });
      await auditLogsService.add({
        timestamp: now,
        actorId: currentUser?.id,
        actorName: currentUser?.name,
        actorRole: currentUser?.role,
        action: 'coupon.applied',
        entityType: 'coupon',
        entityId: coupon.id,
        details: {
          code: coupon.code,
          campaignId: coupon.campaignId,
          discountAmount: discount,
          unitLabel: coupon.unitLabel,
          buildingWing: coupon.buildingWing ?? campaign?.buildingWing,
        },
      });
    } catch (error) {
      const message = (error as Error)?.message || 'Coupon validation failed';
      setCouponError(message);
      try {
        await couponUsageLogsService.create({
          couponCode: normalizedCode,
          staffId: currentUser?.id,
          attemptedAt: Date.now(),
          result: 'rejected',
          reason: message,
        });
      } catch {
        /* best-effort audit; avoid uncaught if rules/network fail */
      }
      try {
        await couponRedemptionsService.create({
          couponCode: normalizedCode,
          couponId: undefined,
          campaignId: undefined,
          staffId: currentUser?.id,
          redeemedAt: Date.now(),
          status: 'rejected',
          reason: message,
        });
      } catch {
        /* best-effort */
      }
      try {
        await auditLogsService.add({
          timestamp: Date.now(),
          actorId: currentUser?.id,
          actorName: currentUser?.name,
          actorRole: currentUser?.role,
          action: 'coupon.rejected',
          entityType: 'coupon',
          details: { code: normalizedCode, reason: message },
        });
      } catch {
        /* best-effort */
      }
    }
  };

  const finalizeCouponRedemption = async (orderId: string) => {
    if (!couponCode) return;
    const coupon = await couponsService.getByCode(couponCode);
    if (!coupon) return;
    await couponsService.markUsed({
      couponId: coupon.id,
      orderId,
      staffId: currentUser?.id,
      discountAmount: couponDiscountAmount,
    });
    await couponUsageLogsService.create({
      couponCode: coupon.code,
      couponId: coupon.id,
      campaignId: coupon.campaignId,
      orderId,
      staffId: currentUser?.id,
      attemptedAt: Date.now(),
      result: 'redeemed',
    });
    await couponRedemptionsService.create({
      couponCode: coupon.code,
      couponId: coupon.id,
      campaignId: coupon.campaignId,
      orderId,
      staffId: currentUser?.id,
      redeemedAt: Date.now(),
      status: 'redeemed',
    });
  };

  const handleSendToKitchen = () => {
    if (!orderType) {
        alert("Please select an order type first.");
        return;
    }
    // For buffet orders, items are not required (buffet is all-inclusive)
    if (orderType !== 'buffet' && orderItems.length === 0) {
        return;
    }
    if ((orderType === 'dine-in' || orderType === 'buffet') && !selectedTable) {
        alert(`Please select a table for the ${orderType === 'buffet' ? 'buffet' : 'dine-in'} order.`);
        return;
    }
    if (orderType === 'uber-doordash' && orderItems.length === 0) {
        alert('Please add at least one item to the order.');
        return;
    }
    if (orderType === 'buffet' && !selectedBuffetType) {
        alert("Please select a buffet type (Breakfast, Lunch, or Dinner).");
        return;
    }
    // Show customer confirmation display instead of directly placing the order
    setIsConfirmationDisplayOpen(true);
  };

  const handleConfirmOrder = async () => {
    if (isPlacingOrder) return;
    if (!orderType) return;
    // For buffet, items are optional (all-inclusive), but for others require items
    if (orderType !== 'buffet' && orderItems.length === 0) return;
    // Uber/DoorDash: place using stored platform + customer name (collected when they clicked Uber/DoorDash)
    if (orderType === 'uber-doordash') {
      if (!uberDoordashDetails) {
        alert('Uber/DoorDash details are missing. Please select Uber/DoorDash again and enter platform and customer name.');
        return;
      }
      setIsPlacingOrder(true);
      try {
        const newOrder = await onPlaceOrder(orderItems, 'uber-doordash', {
          notes,
          manualDiscount: manualDiscount > 0 ? manualDiscount : undefined,
          discountRequiresApproval: discountRequiresApproval || undefined,
          discountApprovedBy: discountApprovedBy,
          discountApprovalReason: discountApprovalReason,
          discountApprovalTimestamp: discountApprovalTimestamp,
          couponCode: couponCode ?? undefined,
          couponCampaignId: couponCampaignId ?? undefined,
          couponDiscountType: couponDiscountType ?? undefined,
          couponDiscountValue: couponDiscountValue || undefined,
          couponDiscountAmount: couponDiscountAmount || undefined,
          couponAppliedByStaffId: currentUser?.id,
          couponAppliedAt: couponCode ? Date.now() : undefined,
          couponUnitLabel: couponCode ? couponUnitLabel : undefined,
          couponBuildingWing: couponCode ? couponBuildingWing : undefined,
          residentDiscountPercent: selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : undefined,
          residentDiscountAmount: selectedCustomer?.isResident ? (((orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0) - (appliedReward?.discountValue ?? 0) - (manualDiscount || 0)) * (selectedCustomer.discountPercentage ?? 0)) / 100) : undefined,
          deliveryInfo: { platform: uberDoordashDetails.platform, customerName: uberDoordashDetails.customerName.trim() || 'Customer' },
        });
        if (newOrder) {
          await finalizeCouponRedemption(newOrder.id);
          setPlacedOrder(newOrder);
          localStorage.removeItem(DRAFT_ORDER_KEY);
          setIsConfirmationDisplayOpen(false);
          setUberDoordashDetails(null);
          clearOrder(true, true);
          setPlacedOrder(null);
          setPosView('pending');
          setActiveView('menu');
        }
      } finally {
        setIsPlacingOrder(false);
      }
      return;
    }
    setIsPlacingOrder(true);
    try {
      // For takeaway/delivery/buffet, we need to process payment immediately
      if (orderType === 'takeaway' || orderType === 'delivery' || orderType === 'buffet') {
        // For buffet, calculate start and end times
        let buffetStartTime: number | undefined;
        let buffetEndTime: number | undefined;
        if (orderType === 'buffet' && selectedBuffetType) {
          buffetStartTime = Date.now();
          buffetEndTime = buffetStartTime + (60 * 60 * 1000); // 1 hour in milliseconds
        }
        
        const servingStaff = selectedServingStaff ?? currentUser;
        // First place the order
        const newOrder = await onPlaceOrder(orderItems, orderType, { 
            tableId: orderType === 'buffet' ? selectedTable?.id : undefined,
            customerId: selectedCustomer?.id,
            customerNumber: orderType === 'buffet' ? customerNumber : undefined,
            customerName: orderType === 'buffet' ? customerName : (orderCustomerName || selectedCustomer?.name),
            deliveryCharge: orderType === 'delivery' ? Math.max(0, deliveryCharge) : undefined,
            notes,
            appliedReward: appliedReward || undefined,
            manualDiscount: discountMethod === 'manual' && manualDiscount > 0 ? manualDiscount : undefined,
            discountMethod,
            firstTableDiscountAmount: discountMethod === 'first-table' && manualDiscount > 0 ? manualDiscount : undefined,
            discountRequiresApproval: discountRequiresApproval || undefined,
            discountApprovedBy: discountApprovedBy,
            discountApprovalReason: discountApprovalReason,
            discountApprovalTimestamp: discountApprovalTimestamp,
            couponCode: couponCode ?? undefined,
            couponCampaignId: couponCampaignId ?? undefined,
            couponDiscountType: couponDiscountType ?? undefined,
            couponDiscountValue: couponDiscountValue || undefined,
            couponDiscountAmount: couponDiscountAmount || undefined,
            couponAppliedByStaffId: currentUser?.id,
            couponAppliedAt: couponCode ? Date.now() : undefined,
          couponUnitLabel: couponCode ? couponUnitLabel : undefined,
          couponBuildingWing: couponCode ? couponBuildingWing : undefined,
            residentDiscountPercent: selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : undefined,
            residentDiscountAmount: selectedCustomer?.isResident ? (((orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0) - (appliedReward?.discountValue ?? 0) - (manualDiscount || 0)) * (selectedCustomer.discountPercentage ?? 0)) / 100) : undefined,
            buffetType: orderType === 'buffet' ? selectedBuffetType : undefined,
            buffetStartTime: buffetStartTime,
            buffetEndTime: buffetEndTime,
            ...(servingStaff && (orderType === 'dine-in' || orderType === 'buffet') ? { servingStaffId: servingStaff.id, servingStaffName: servingStaff.name } : {}),
        });
        
        if (newOrder) {
            await finalizeCouponRedemption(newOrder.id);
            setPlacedOrder(newOrder);
            localStorage.removeItem(DRAFT_ORDER_KEY);
            setIsConfirmationDisplayOpen(false);
            // Open payment modal immediately for takeaway/delivery
            setIsPaymentModalOpen(true);
        }
      } else {
        // For dine-in, just place the order (payment happens later)
        const servingStaff = selectedServingStaff ?? currentUser;
        const newOrder = await onPlaceOrder(orderItems, orderType, { 
            tableId: selectedTable?.id, 
            customerId: selectedCustomer?.id,
            customerNumber: customerNumber,
            customerName: customerName,
            notes,
            appliedReward: appliedReward || undefined,
            manualDiscount: discountMethod === 'manual' && manualDiscount > 0 ? manualDiscount : undefined,
            discountMethod,
            firstTableDiscountAmount: discountMethod === 'first-table' && manualDiscount > 0 ? manualDiscount : undefined,
            discountRequiresApproval: discountRequiresApproval || undefined,
            discountApprovedBy: discountApprovedBy,
            discountApprovalReason: discountApprovalReason,
            discountApprovalTimestamp: discountApprovalTimestamp,
            couponCode: couponCode ?? undefined,
            couponCampaignId: couponCampaignId ?? undefined,
            couponDiscountType: couponDiscountType ?? undefined,
            couponDiscountValue: couponDiscountValue || undefined,
            couponDiscountAmount: couponDiscountAmount || undefined,
            couponAppliedByStaffId: currentUser?.id,
            couponAppliedAt: couponCode ? Date.now() : undefined,
          couponUnitLabel: couponCode ? couponUnitLabel : undefined,
          couponBuildingWing: couponCode ? couponBuildingWing : undefined,
            residentDiscountPercent: selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : undefined,
            residentDiscountAmount: selectedCustomer?.isResident ? (((orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0) - (appliedReward?.discountValue ?? 0) - (manualDiscount || 0)) * (selectedCustomer.discountPercentage ?? 0)) / 100) : undefined,
            ...(servingStaff && { servingStaffId: servingStaff.id, servingStaffName: servingStaff.name }),
        });
        
        if (newOrder) {
            await finalizeCouponRedemption(newOrder.id);
            setPlacedOrder(newOrder);
            localStorage.removeItem(DRAFT_ORDER_KEY);
            setIsConfirmationDisplayOpen(false);
            // Clear cart but keep table selected and stay on floor so order stays visible
            clearOrder(false, false); // don't clear table, don't reset order type (keeps dine-in)
            setPlacedOrder(null);
            setPosView('floor');
            setActiveView('floor');
            // Keep orderType as 'dine-in' so floor plan remains visible
        }
      }
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleCancelConfirmation = () => {
    setIsConfirmationDisplayOpen(false);
  };

  const handleMarkUberDoordashPickedUp = (order: Order) => {
    if (onUpdateOrderStatus) {
      onUpdateOrderStatus(order.id, 'served');
      return;
    }
    const { totalAmount } = getOrderTotals(order);
    const payments = order.payments?.length
      ? order.payments
      : [{ method: 'card' as const, amount: totalAmount, timestamp: Date.now() }];
    onUpdateOrder({
      ...order,
      status: 'served',
      paymentStatus: 'paid',
      payments,
    });
  };

  const handleSendUberReady = useCallback(async (order: Order) => {
    const uberOrderId = order.deliveryInfo?.orderId;
    const now = Date.now();

    // Manual Uber orders created in POS won't have an upstream Uber order ID yet.
    // Allow those to progress locally while preserving API sync for real Uber orders.
    if (!uberOrderId) {
      onUpdateOrder({
        ...order,
        status: 'ready',
        readyAt: now,
        deliveryInfo: {
          ...order.deliveryInfo,
          uberReadySentAt: now,
        },
      });
      return;
    }
    try {
      await markUberEatsOrderReady(uberOrderId);
      onUpdateOrder({
        ...order,
        status: 'ready',
        readyAt: now,
        deliveryInfo: {
          ...order.deliveryInfo,
          uberReadySentAt: now,
        },
      });
    } catch (err) {
      console.error('markUberEatsOrderReady failed:', err);
      alert((err as any)?.message || 'Failed to mark order as ready in Uber.');
    }
  }, [onUpdateOrder]);

  const handleUberDoordashModalSubmit = (platform: 'Uber Eats' | 'DoorDash', customerName: string) => {
    setUberDoordashDetails({ platform, customerName: customerName.trim() || 'Customer' });
    setOrderType('uber-doordash');
    setShowUberDoordashModal(false);
    setActiveView('menu');
  };
  
  const handleUpdateOrder = () => {
    if (!placedOrder) return;
    
    // For takeaway/delivery orders, don't allow updates - they should create new orders
    // since payment is processed upfront (or for Uber/DoorDash, payment is when picked up)
    if (placedOrder.orderType === 'takeaway' || placedOrder.orderType === 'delivery' || placedOrder.orderType === 'uber-doordash') {
      alert('Cannot update takeaway, delivery, or Uber/DoorDash orders. Please create a new order.');
      return;
    }
    
    // Only allow updates for unpaid orders (dine-in orders before payment)
    if (placedOrder.paymentStatus === 'paid') {
      alert('Cannot update paid orders.');
      return;
    }
    
    // Ensure new items have proper status and served flag; match existing items by lineId when available
    const processedItems = orderItems.map(item => {
      let existingItem = null;
      if (item.lineId) {
        existingItem = placedOrder.items.find(oi => oi.lineId === item.lineId);
      }
      if (!existingItem) {
        existingItem = placedOrder.items.find(oi =>
          oi.id === item.id &&
          oi.selectedMeatOption === item.selectedMeatOption
        );
      }
      if (!existingItem) {
        return {
          ...item,
          status: 'pending' as const,
          served: false,
        };
      }
      return {
        ...item,
        status: existingItem.status,
        served: existingItem.served,
      };
    });
    
    const orderWithItems = { ...placedOrder, items: processedItems, appliedReward: appliedReward || undefined };
    const { subtotal, taxAmount, totalAmount } = getOrderTotals(orderWithItems);
    const updatedOrder: Order = {
        ...placedOrder,
        items: processedItems,
        notes: notes || undefined,
        customerId: selectedCustomer?.id,
        customerNumber: customerNumber,
        customerName: customerName,
        appliedReward: appliedReward || undefined,
        ...(selectedServingStaff && (placedOrder.orderType === 'dine-in' || placedOrder.orderType === 'buffet') ? { servingStaffId: selectedServingStaff.id, servingStaffName: selectedServingStaff.name } : {}),
        subtotal,
        taxAmount,
        totalAmount,
    };
    
    onUpdateOrder(updatedOrder);
    setPlacedOrder(updatedOrder);
    alert("Order updated successfully!");
  };

  // Check if manager approval is required for cancellation
  const requiresManagerApproval = (order: Order): boolean => {
    // If order is 'new' or 'queued' (not started cooking), no approval needed
    if (order.status === 'new' || order.status === 'queued') {
      return false;
    }
    
    // If order has started preparing or is already served, approval is required
    if (order.status === 'preparing' || order.status === 'ready' || order.status === 'served') {
      return true;
    }
    
    // If customer doesn't have money after having food, approval is required
    // This is a special case that will be passed via cancellationReason
    // For now, if order is paid but we're cancelling, it might be this case
    if (order.paymentStatus === 'paid' && order.status !== 'new' && order.status !== 'queued') {
      return true;
    }
    
    return false;
  };

  const handleCancelOrder = async (orderOrEvent?: Order | MouseEvent<HTMLButtonElement>, reason?: string) => {
    // Handle case where this is called from a button click (event object) or with an order
    let orderToCancel: Order | undefined;
    
    // Check if first argument is an Order object (has 'id' property and is not an event)
    // Events have 'nativeEvent', 'target', 'currentTarget' properties
    // Orders have 'id', 'items', 'status' properties
    if (orderOrEvent && typeof orderOrEvent === 'object') {
      if ('id' in orderOrEvent && 'items' in orderOrEvent && !('nativeEvent' in orderOrEvent)) {
        // It's an Order object
        orderToCancel = orderOrEvent as Order;
      }
      // If it's an event (has nativeEvent or target), ignore it and use resolvedPlacedOrder
    }
    
    // If we don't have an order yet, use resolvedPlacedOrder
    if (!orderToCancel) {
      orderToCancel = resolvedPlacedOrder || null;
    }
    
    // If we still don't have an order, try placedOrder as last resort
    if (!orderToCancel && placedOrder) {
      orderToCancel = placedOrder;
    }
    
    // If we have an order with ID, try to get the full order from the orders array
    if (orderToCancel && orderToCancel.id) {
      const fullOrder = orders.find(o => o.id === orderToCancel!.id);
      if (fullOrder) {
        orderToCancel = fullOrder;
      }
    }
    
    if (!orderToCancel) {
      console.error('❌ Cannot cancel: No order found', {
        hasPlacedOrder: !!placedOrder,
        placedOrderId: placedOrder?.id,
        hasResolvedPlacedOrder: !!resolvedPlacedOrder,
        resolvedPlacedOrderId: resolvedPlacedOrder?.id,
        ordersCount: orders.length,
        orderIds: orders.map(o => o.id).slice(0, 5)
      });
      alert('Cannot cancel order: Order not found. Please try again.');
      return;
    }
    
    // Ensure we have a valid order with ID
    if (!orderToCancel.id) {
      console.error('❌ Cannot cancel: Order missing ID', {
        order: orderToCancel,
        hasId: !!orderToCancel.id,
        keys: Object.keys(orderToCancel),
        placedOrderId: placedOrder?.id,
        resolvedPlacedOrderId: resolvedPlacedOrder?.id
      });
      alert('Cannot cancel order: Order ID is missing. Please try again.');
      return;
    }
    
    console.log('🔄 Attempting to cancel order:', {
      orderId: orderToCancel.id,
      status: orderToCancel.status,
      paymentStatus: orderToCancel.paymentStatus,
      itemsCount: orderToCancel.items?.length || 0
    });
    
    const needsApproval = requiresManagerApproval(orderToCancel);
    
    // If approval is needed, show the approval modal (even for managers - they need to enter PIN and reason)
    if (needsApproval) {
      setPendingCancelOrder(orderToCancel);
      setCancelReason(reason || '');
      setShowCancelApprovalModal(true);
      return;
    }
    
    // If no approval needed, proceed with cancellation
    await processCancellation(orderToCancel, reason || 'Order cancelled');
  };

  const processCancellation = async (order: Order, reason: string, approvedBy?: number) => {
    try {
      // Validate order has required properties
      if (!order || !order.id) {
        throw new Error('Invalid order: order ID is missing');
      }
      
      // Ensure items array exists
      if (!order.items || !Array.isArray(order.items)) {
        console.warn('⚠️ Order missing items array, using empty array:', order.id);
        order.items = [];
      }
      
      // Determine if this is a "customer doesn't have money" scenario
      const isNoMoneyScenario = reason.toLowerCase().includes("doesn't have money") || 
                                reason.toLowerCase().includes("no money") ||
                                reason.toLowerCase().includes("can't pay") ||
                                reason.toLowerCase().includes("cannot pay") ||
                                reason.toLowerCase().includes("unable to pay");
      
      // Update order status
      const updates: Partial<Order> = {
        status: 'cancelled',
        // If customer doesn't have money after having food, mark as unpaid
        // If order was already paid and cancelled for other reasons, keep as paid (refund handled separately if needed)
        paymentStatus: isNoMoneyScenario ? 'unpaid' : order.paymentStatus,
      };
      
      // Store cancellation details
      (updates as any).cancelledAt = Date.now();
      (updates as any).cancellationReason = reason;
      if (approvedBy) {
        (updates as any).cancelledBy = approvedBy;
      }
      
      // Ensure items array is preserved when cancelling
      const orderToUpdate: Order = {
        ...order,
        ...updates,
        // Explicitly preserve items array to prevent undefined errors
        items: order.items || [],
        // Ensure other required fields are present
        payments: order.payments || [],
        subtotal: order.subtotal || 0,
        taxAmount: order.taxAmount || 0,
        totalAmount: order.totalAmount || 0,
      };
      
      console.log('🔄 Cancelling order:', {
        orderId: orderToUpdate.id,
        status: orderToUpdate.status,
        paymentStatus: orderToUpdate.paymentStatus,
        orderType: orderToUpdate.orderType,
        tableId: orderToUpdate.tableId,
        itemsCount: orderToUpdate.items.length,
        reason: reason
      });
      
      await onUpdateOrder(orderToUpdate);
      
      console.log('✅ Order cancellation update sent:', {
        orderId: orderToUpdate.id,
        status: orderToUpdate.status,
        tableId: orderToUpdate.tableId
      });

      // Note: Table release will be handled in App.tsx handleUpdateOrder
      // when it detects the order status is 'cancelled'
      // This ensures the table is released after the order update completes

      // If this was the placed order, clear it
      if (placedOrder && placedOrder.id === order.id) {
        clearOrder();
        setPlacedOrder(null);
        setPosView('cancelled'); // Switch to cancelled tab
        setOrderType(null);
      }
      
      alert("Order cancelled successfully!");
    } catch (error) {
      console.error('Failed to cancel order:', error);
      alert('Failed to cancel order. Please try again.');
    }
  };

  const handleCancelApproval = (staffId: number, reason: string) => {
    if (pendingCancelOrder) {
      processCancellation(pendingCancelOrder, reason, staffId);
      setPendingCancelOrder(null);
      setCancelReason('');
      setShowCancelApprovalModal(false);
    }
  };

  const handleReleaseTable = async () => {
    if (!selectedTable || orderItems.length > 0) return;
    
    if (!confirm(`Release table ${selectedTable.name}?`)) {
      return;
    }

    const todayKey = new Date().toDateString();

    // Find all unpaid dine-in orders for this table (multiple customers)
    const activeDineInOrders = orders.filter(o => 
      o.orderType === 'dine-in' && 
      o.tableId === selectedTable.id && 
      o.paymentStatus === 'unpaid' &&
      (o.status === 'new' || o.status === 'preparing' || o.status === 'ready' || o.status === 'served') &&
      new Date(o.timestamp).toDateString() === todayKey
    );

    if (activeDineInOrders.length > 0) {
      // Check if all orders are empty (no items)
      const allEmpty = activeDineInOrders.every(o => o.items.length === 0);
      
      if (allEmpty && onRemoveOrder) {
        // Cancel all empty orders
        try {
          await Promise.all(activeDineInOrders.map(o => onRemoveOrder(o.id)));
        } catch (error) {
          console.error('Failed to cancel orders:', error);
        }
      } else {
        alert(`Cannot release table. There are ${activeDineInOrders.length} unpaid dine-in order(s) with items for this table.`);
        return;
      }
    }

    // Mark buffet sessions as completed when releasing table
    const buffetSessions = orders.filter(o => 
      o.orderType === 'buffet' && 
      o.tableId === selectedTable.id && 
      o.status === 'buffet'
    );

    if (buffetSessions.length > 0) {
      try {
        await Promise.all(buffetSessions.map(order => onUpdateOrder?.({
          ...order,
          status: 'served' as OrderStatus
        })));
      } catch (error) {
        console.error('Failed to finalize buffet sessions:', error);
      }
    }

    // Release table - clear selection
    setSelectedTable(null);
    clearOrder();
    alert(`Table ${selectedTable.name} released successfully!`);
  };

  const handleSelectTableCustomer = (customerNum: number, customerNameStr?: string, existingOrder?: Order) => {
    if (!pendingTableSelection) return;
    
    if (existingOrder) {
      // Load existing order
      setPlacedOrder(existingOrder);
      setOrderItems(existingOrder.items);
      setNotes(existingOrder.notes || '');
      setSelectedTable(pendingTableSelection);
      setCustomerNumber(existingOrder.customerNumber);
      setCustomerName(existingOrder.customerName);
      const customer = customers.find(c => c.id === existingOrder.customerId);
      setSelectedCustomer(customer || null);
      setAppliedReward(existingOrder.appliedReward || null);
      setOrderType(existingOrder.orderType);
      setPosView('new-order');
      setActiveView('menu');
    } else {
      // Start new order for this customer (seat new party at table with spare capacity)
      const wasBuffet = orderType === 'buffet' && selectedBuffetType;
      clearOrder(true, !wasBuffet); // preserve orderType/selectedBuffetType when adding buffet party
      setSelectedTable(pendingTableSelection);
      setSelectedServingStaff(currentUser ?? null);
      setCustomerNumber(customerNum);
      setCustomerName(customerNameStr);
      if (wasBuffet) {
        // Buffet flow: covers → payment → track time
        setOrderType('buffet');
        setSelectedBuffetType(selectedBuffetType!);
        setPosView('new-order');
        setActiveView('menu');
        setShowBuffetPeopleModal(true);
      } else {
        setOrderType('dine-in');
        setPosView('new-order');
        setActiveView('menu');
      }
    }
    
    setPendingTableSelection(null);
    setShowTableCustomerModal(false);
  };

  const handleSeatNewDineInParty = (covers: number) => {
    if (!pendingDineInTable) return;

    const todayKey = new Date().toDateString();
    const existingOrders = orders.filter(o => {
      if (o.tableId !== pendingDineInTable.id) return false;
      if (o.status === 'cancelled' || o.status === 'refunded') return false;
      const orderDateKey = new Date(o.timestamp).toDateString();
      if (orderDateKey !== todayKey) return false;
      return o.customerNumber !== undefined;
    });
    const nextCustomerNumber = existingOrders.length > 0
      ? Math.max(...existingOrders.map(o => o.customerNumber!)) + 1
      : 1;

    clearOrder();
    setSelectedTable(pendingDineInTable);
    setSelectedServingStaff(currentUser ?? null);
    setOrderType('dine-in');
    setCustomerNumber(nextCustomerNumber);
    setCustomerName(undefined);
    setNotes(`Party of ${covers}`);
    setPosView('new-order');
    setActiveView('menu');

    setPendingDineInTable(null);
    setPendingDineInSpareSeats(0);
    setShowDineInCoversModal(false);
  };

  // Table merge/split/transfer handlers
  const handleToggleMergeMode = () => {
    setIsMergeMode(!isMergeMode);
    setSelectedTableIds([]);
    setSelectedTableForSplit(null);
  };

  const handleTableSelect = (tableId: number) => {
    if (!isMergeMode) {
      // If not in merge mode, check if this is a merged table for splitting
      const table = tables.find(t => Number(t.id) === Number(tableId));
      if (table && table.mergedWith && table.mergedWith.length > 1) {
        const primaryTableId = table.mergedWith[0];
        const isPrimaryMerged = Number(primaryTableId) === Number(table.id);
        if (isPrimaryMerged) {
          setSelectedTableForSplit(table);
        } else {
          // If clicking a secondary merged table, find the primary one
          const primaryTable = tables.find(t => Number(t.id) === Number(primaryTableId));
          if (primaryTable) {
            setSelectedTableForSplit(primaryTable);
          } else {
            setSelectedTableForSplit(null);
          }
        }
      } else {
        setSelectedTableForSplit(null);
      }
      return;
    }
    setSelectedTableIds(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const handleMergeClick = () => {
    if (selectedTableIds.length < 2) {
      alert('Please select at least 2 tables to merge.');
      return;
    }
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (onMergeTables && selectedTableIds.length >= 2) {
      await onMergeTables(selectedTableIds);
      setSelectedTableIds([]);
      setIsMergeMode(false);
      setShowMergeModal(false);
    }
  };

  const handleSplitTable = (tableId: number) => {
    const table = tables.find(t => Number(t.id) === Number(tableId));
    if (table && table.mergedWith && table.mergedWith.length > 1) {
      setTableToSplit(table);
      setShowSplitModal(true);
    }
  };

  const handleConfirmSplit = async () => {
    if (onSplitTables && tableToSplit) {
      await onSplitTables(tableToSplit.id);
      setTableToSplit(null);
      setSelectedTableForSplit(null);
      setSelectedTableIds([]);
      setShowSplitModal(false);
    }
  };

  const handleInitiateTransfer = (order: Order) => {
    const table = tables.find(t => t.id === order.tableId);
    if (table) {
      setOrderToTransfer({ order, table });
      setShowTransferModal(true);
    }
  };

  const handleConfirmTransfer = async (targetTableId: number) => {
    if (onTransferOrder && orderToTransfer) {
      await onTransferOrder(orderToTransfer.order.id, targetTableId);
      setOrderToTransfer(null);
      setShowTransferModal(false);
    }
  };

  // Override order handler
  const handleInitiateOverride = (order: Order) => {
    setOrderToOverride(order);
    setShowOverrideModal(true);
  };

  const handleConfirmOverride = async (reason: string) => {
    if (onOverrideOrder && orderToOverride) {
      await onOverrideOrder(orderToOverride.id, reason);
      setOrderToOverride(null);
      setShowOverrideModal(false);
      setSelectedHistoryOrder(null);
    }
  };

  // Remake order handler
  const handleInitiateRemake = (order: Order) => {
    setOrderToRemake(order);
    setShowRemakeModal(true);
  };

  const handleConfirmRemake = async (items: OrderItem[], reason: string) => {
    if (onRemakeOrder && orderToRemake) {
      await onRemakeOrder(orderToRemake.id, items, reason);
      setOrderToRemake(null);
      setShowRemakeModal(false);
      // Refresh order view
      if (orderToRemake.id === placedOrder?.id) {
        const updatedOrder = orders.find(o => o.id === orderToRemake.id);
        if (updatedOrder) {
          setPlacedOrder(updatedOrder);
          setOrderItems(updatedOrder.items);
        }
      }
    }
  };

  // Buffet extension handlers
  const handleInitiateBuffetExtension = (order: Order) => {
    setOrderToExtend(order);
    setShowBuffetExtensionModal(true);
  };

  const handleConfirmBuffetExtension = async (extensionMinutes: number, reason?: string) => {
    if (onExtendBuffetTime && orderToExtend) {
      await onExtendBuffetTime(orderToExtend.id, extensionMinutes, reason);
      setOrderToExtend(null);
      setShowBuffetExtensionModal(false);
      // Refresh order view
      if (orderToExtend.id === placedOrder?.id) {
        const updatedOrder = orders.find(o => o.id === orderToExtend.id);
        if (updatedOrder) {
          setPlacedOrder(updatedOrder);
          setOrderItems(updatedOrder.items);
        }
      }
    }
  };

  // Fire mode handlers
  const handleToggleFireMode = () => {
    setShowFireModeModal(true);
  };

  const handleConfirmFireModeActivate = async () => {
    if (onActivateFireMode) {
      await onActivateFireMode();
      setShowFireModeModal(false);
    }
  };

  const handleConfirmFireModeDeactivate = async () => {
    if (onDeactivateFireMode) {
      await onDeactivateFireMode();
      setShowFireModeModal(false);
    }
  };

  const handleInitiatePayment = () => {
    if (!resolvedPlacedOrder) return;
    setIsPaymentModalOpen(true);
  };
  
  const handleClosePaymentModal = async () => {
      setIsPaymentModalOpen(false);
      setShouldAutoOpenPaymentModal(false);
      if (orderFullySettled) {
         // Buffet: keep the single order on the table (paid, status 'buffet') until session ends. Go to floor.
         if (orderType === 'buffet') {
           setPlacedOrder(null);
           clearOrder(false, false);
           setPosView('floor');
           setActiveView('floor');
           setOrderType('buffet');
           setSelectedBuffetType(selectedBuffetType ?? undefined);
           setBuffetNumberOfPeople(buffetNumberOfPeople ?? 1);
           setSelectedTable(selectedTable ?? undefined);
           setSelectedCustomer(selectedCustomer ?? undefined);
         } else {
           setPlacedOrder(null);
           clearOrder();
           setPosView('new-order');
           setActiveView('menu');
           setOrderType(null);
           setSelectedTable(null);
           setSelectedCustomer(null);
           setSelectedBuffetType(null);
           setBuffetNumberOfPeople(1);
         }
      }
  }

  const handlePrintReceipt = (order: Order) => {
    setPrintPendingOrder(order);
  };

  const handlePrintTypeChosen = (type: 'customer' | 'kitchen') => {
    if (!printPendingOrder) return;
    onAuditPrint?.(type, printPendingOrder);
    setOrderToPrint(printPendingOrder);
    setPrintType(type);
    setPrintPendingOrder(null);
  };
  
  const handleFinalNewOrder = () => {
    setIsPaymentModalOpen(false);
    setPlacedOrder(null);
    clearOrder();
    setPosView('new-order');
    setOrderType(null);
    setSelectedTable(null);
    setSelectedCustomer(null);
  };

  // Filter menu items by category and global availability (compare as string so Firestore string ids match)
  const displayedItems = selectedCategoryId != null && selectedCategoryId !== ''
    ? menuItems.filter(item => {
        const matchesCategory = String(item.categoryId) === String(selectedCategoryId);
        const isGloballyAvailable = item.isAvailable !== false;
        return matchesCategory && isGloballyAvailable;
      })
    : [];

  // Apply menu search: when query is set, search across all categories by name/description
  const searchTrimmed = menuSearchQuery.trim().toLowerCase();
  const displayedItemsFiltered = searchTrimmed
    ? menuItems.filter(item => {
        const isGloballyAvailable = item.isAvailable !== false;
        const nameMatch = item.name?.toLowerCase().includes(searchTrimmed);
        const descMatch = item.description?.toLowerCase().includes(searchTrimmed);
        return isGloballyAvailable && (nameMatch || descMatch);
      })
    : displayedItems;
  
  // Comprehensive menu debug logging (opt-in in dev via localStorage POS_MENU_DEBUG=1)
  useEffect(() => {
    if (orderType && activeView === 'menu' && selectedCategoryId) {
      const debugEnabled = import.meta.env.DEV && window.localStorage.getItem('POS_MENU_DEBUG') === '1';
      // Get all unique categoryIds from menu items
      const uniqueCategoryIds = [...new Set(menuItems.map(item => {
        const id = typeof item.categoryId === 'string' ? parseInt(item.categoryId, 10) : item.categoryId;
        return id;
      }))].sort((a, b) => a - b);
      
      // Get all category IDs
      const categoryIds = categories.map(c => {
        const id = typeof c.id === 'string' ? parseInt(c.id, 10) : c.id;
        return id;
      }).sort((a, b) => a - b);
      
      // Check for matches
      const matchingItems = menuItems.filter(item => {
        const itemCategoryId = typeof item.categoryId === 'string' ? parseInt(item.categoryId, 10) : item.categoryId;
        const selectedId = typeof selectedCategoryId === 'string' ? parseInt(selectedCategoryId, 10) : selectedCategoryId;
        return itemCategoryId === selectedId;
      });
      
      if (debugEnabled) {
        console.log('🔍 DETAILED MENU DEBUG:', {
          selectedCategoryId,
          selectedCategoryIdType: typeof selectedCategoryId,
          categoriesCount: categories.length,
          categoryIds: categoryIds,
          menuItemsCount: menuItems.length,
          uniqueCategoryIdsInMenuItems: uniqueCategoryIds,
          displayedItemsCount: displayedItems.length,
          matchingItemsCount: matchingItems.length,
          sampleCategories: categories.slice(0, 3).map(c => ({ id: c.id, name: c.name, idType: typeof c.id })),
          sampleMenuItems: menuItems.slice(0, 5).map(item => ({
            name: item.name,
            categoryId: item.categoryId,
            categoryIdType: typeof item.categoryId
          })),
          itemsForSelectedCategory: matchingItems.slice(0, 3).map(item => ({
            name: item.name,
            categoryId: item.categoryId
          }))
        });
      }
      
      // Check for mismatches
      const mismatches = uniqueCategoryIds.filter(catId => Number.isFinite(catId) && catId > 0 && !categoryIds.includes(catId));
      if (mismatches.length > 0) {
        console.warn('⚠️ WARNING: Menu items reference category IDs that don\'t exist:', mismatches);
      }
      
      if (displayedItems.length === 0 && menuItems.length > 0) {
        console.error('❌ NO ITEMS DISPLAYED!', {
          reason: 'Filter returned 0 items',
          selectedCategoryId,
          availableCategoryIds: categoryIds,
          menuItemCategoryIds: uniqueCategoryIds,
          possibleIssue: mismatches.length > 0 ? 'Category ID mismatch' : 'ID type mismatch or no items for this category'
        });
      }
    }
  }, [categories, menuItems, selectedCategoryId, displayedItems.length, orderType, activeView]);
  const historyOrderCustomer = selectedHistoryOrder ? customers.find(c => c.id === selectedHistoryOrder.customerId) : null;

  // Calculate order totals (exclude removed items)
  const calculateOrderTotals = () => {
    const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
    const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
    const residentDiscountPercent = selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : 0;
    const residentDiscountAmount = residentDiscountPercent > 0
      ? ((totalBeforeDiscount - rewardDiscount - manualDiscount) * residentDiscountPercent) / 100
      : 0;
    let totalAfterDiscount = totalBeforeDiscount - rewardDiscount - manualDiscount - residentDiscountAmount;
    if (orderType === 'delivery') {
      totalAfterDiscount += Math.max(0, deliveryCharge);
    }
    if (orderType === 'uber-doordash') {
      const pct = integrationSettings?.uberDoordashPriceIncreasePercent ?? 0;
      const multiplier = 1 + pct / 100;
      totalAfterDiscount = totalAfterDiscount * multiplier;
    }
    const tax = totalAfterDiscount * (15 / 115);
    const subtotal = totalAfterDiscount - tax;
    return { subtotal, tax, totalAfterDiscount };
  };

  const { subtotal: orderSubtotal, tax: orderTax, totalAfterDiscount: orderTotal } = calculateOrderTotals();

  // If register is not open, show guard
  if (!registerSession) {

    return (
      <div className="flex flex-col h-full bg-gray-50">
        <POSHeader 
          registerSession={registerSession}
          activeSessions={activeSessions}
          terminalId={terminalId}
          terminalName={terminalName}
          onTerminalNameChange={onTerminalNameChange || (() => {})}
          todaysOrders={todaysOrders}
          sessionOrders={sessionOrders}
          inventoryItems={inventoryItems}
          recipes={recipes}
          onOpenRegister={onOpenRegister}
          onCloseRegister={onCloseRegister}
          onStockTake={onStockTake}
          onForceCloseSession={onForceCloseSession}
          onRequireManagerPin={onRequireManagerPin}
          currentUser={currentUser}
          showForceClose={showForceClose}
          onUpdateOrder={onUpdateOrder}
          onMarkAsPaid={onMarkAsPaid}
        />
        <RegisterClosedGuard 
          onOpenRegister={onOpenRegister}
          todaysOrders={todaysOrders}
          currentUser={currentUser}
          isLoading={!registerSessionsHydrated}
        />
      </div>
    );
  }

  // Check if user is manager
  const isManager = currentUser && (
    (currentUser as any).permissions?.has('access_reports') || 
    (currentUser as any).permissions?.has('manage_staff') ||
    currentUser.isAdmin
  );

  return (
    <>
      <FireModeBanner isActive={fireModeActive || false} />
      <div className="flex flex-col h-full bg-gray-50">
      <POSHeader 
        registerSession={registerSession}
        activeSessions={activeSessions}
        terminalId={terminalId}
        terminalName={terminalName}
        onTerminalNameChange={onTerminalNameChange || (() => {})}
        sessionOrders={sessionOrders}
        todaysOrders={todaysOrders}
        inventoryItems={inventoryItems}
        recipes={recipes}
        onOpenRegister={onOpenRegister}
        onCloseRegister={onCloseRegister}
        onStockTake={onStockTake}
        onForceCloseSession={onForceCloseSession}
        onRequireManagerPin={onRequireManagerPin}
        currentUser={currentUser}
        showForceClose={showForceClose}
        onUpdateOrder={onUpdateOrder}
        onMarkAsPaid={onMarkAsPaid}
      />
      {uberPendingOrders.length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-300 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-yellow-800">
              Uber / DoorDash incoming orders: {uberPendingOrders.length}
            </p>
            <p className="text-xs text-yellow-700">
              Review and accept or reject these delivery orders here. They will appear in POS and kitchen once accepted.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
             {/* Main Navigation Tabs */}
             <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-between gap-2 min-h-0">
                <div className="flex items-center gap-2">
                {hasActiveOrders && (
                  <button 
                    onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('active-orders');
                    setActiveView('menu');
                  }}
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'active-orders' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    📋 Active Orders ({todaysOrders.length})
                  </button>
                )}
                <button 
                  onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setSelectedTable(null);
                    setPosView('new-order');
                    setOrderType(null);
                    setActiveView('floor');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'new-order' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ➕ New Order
                </button>
                <button 
                  onClick={() => {
                    clearOrder();
                    setPlacedOrder(null);
                    setSelectedTable(null);
                    setPosView('floor'); 
                    setOrderType(prev => (prev === 'buffet' ? 'buffet' : 'dine-in'));
                    setActiveView('floor'); 
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'floor' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  🏪 Floor Plan
                </button>
                <button 
                  onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('pending');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'pending' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ⏳ Pending
                </button>
                <button 
                  onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('completed');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'completed' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ✅ Completed
                </button>
                <button 
                  onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('cancelled');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'cancelled' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ❌ Cancelled ({cancelledTabBadgeCount})
                </button>
                <button 
                  onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('refunded');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'refunded' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  💸 Refunded ({refundedTabBadgeCount})
                </button>
                </div>
            </div>
            
            {/* View-specific navigation for ordering */}
            {(posView === 'new-order' || posView === 'floor') && !orderType && (
              <div className="px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 border-b border-pink-200">
                <p className="text-xs text-gray-600 mb-2">Select order type:</p>
                <div className="flex gap-2">
                  <button onClick={() => handleSelectOrderType('dine-in')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🪑 Dine-In</button>
                  <button onClick={() => handleSelectOrderType('takeaway')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🛍️ Takeaway</button>
                  <button onClick={() => handleSelectOrderType('delivery')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🚚 Delivery</button>
                  <button onClick={() => handleSelectOrderType('buffet')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🍽️ Buffet</button>
                  <button onClick={() => handleSelectOrderType('uber-doordash')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">📦 Uber/DoorDash</button>
                </div>
              </div>
            )}
            
            {/* Refunded Orders View */}
            {posView === 'refunded' && activeView === 'menu' && (() => {
              const refundedSource = mergeOrderSources(historyOrdersFetched, orders);
              const filteredRefunded = filterOrdersByHistoryDate(
                refundedSource.filter(
                  (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'refunded',
                ),
                historyOrdersDateFilter,
                historyDateFilterOpts,
              );

              return (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-800">Refunded Orders</h2>
                      <HistoryOrdersDateFilterBar
                        filter={historyOrdersDateFilter}
                        onFilterChange={setHistoryOrdersDateFilter}
                        customDateStart={customDateStart}
                        customDateEnd={customDateEnd}
                        onCustomDateStartChange={setCustomDateStart}
                        onCustomDateEndChange={setCustomDateEnd}
                      />
                    </div>
                    {historyOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading orders…</p>
                    ) : filteredRefunded.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No refunded orders found for the selected period.
                      </p>
                    ) : (
                      <div className={POS_ORDER_LIST_GRID_CLASS}>
                        {filteredRefunded.map(order => (
                          <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-gray-500 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { setSelectedHistoryOrder(order); }}>
                                <p className="font-bold text-sm">
                                  {order.orderType === 'buffet'
                                    ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                    : order.orderType === 'dine-in'
                                    ? (order.tableName || (() => {
                                        const table = tables.find(t => t.id === order.tableId);
                                        return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                      })())
                                    : order.orderType === 'takeaway'
                                    ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`)
                                    : order.orderType === 'online'
                                    ? `Online Order`
                                    : order.orderType === 'event'
                                    ? `Event`
                                    : order.orderType === 'uber-doordash'
                                    ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                    : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                                </p>
                                {/* Only show customer name on second line when not already in title (e.g. Uber/DoorDash, dine-in) */}
                                {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                  <p className="text-sm text-pink-600 font-semibold">{order.customerName || order.deliveryInfo?.customerName}</p>
                                )}
                                <p className="text-xs text-gray-600">Order ID: {order.id}</p>
                                <p className="text-xs text-gray-500">
                                  {(() => {
                                    let orderDate: Date;
                                    if (typeof order.timestamp === 'number') {
                                      orderDate = new Date(order.timestamp);
                                    } else if (order.timestamp instanceof Date) {
                                      orderDate = order.timestamp;
                                    } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                      orderDate = new Date((order.timestamp as any).seconds * 1000);
                                    } else {
                                      return 'Invalid Date';
                                    }
                                    if (isNaN(orderDate.getTime())) {
                                      return 'Invalid Date';
                                    }
                                    return orderDate.toLocaleString('en-NZ', {
                                      timeZone: 'Pacific/Auckland',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                  })()}
                                </p>
                              </div>
                              <span className="text-xs font-bold px-2 py-1 rounded bg-gray-100 text-gray-700">
                                REFUNDED
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { setSelectedHistoryOrder(order); }}>
                                <p className="text-xs text-gray-600">{order.items.length} items • ${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                                <p className="text-xs text-gray-500 mt-1 font-bold">💸 Refund processed</p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSelectedHistoryOrder(order); }}
                                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                >
                                  Details
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </main>
              );
            })()}
            {posView === 'active-orders' && activeView === 'menu' && (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className="max-w-4xl mx-auto space-y-3">
                    <h2 className="text-lg font-bold text-gray-800">Active Orders Today</h2>
                    <div className="grid gap-3 md:grid-cols-2">
                      {todaysOrders.map(order => (
                        <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-pink-500 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 cursor-pointer" onClick={() => handleSelectPendingOrder(order)}>
                                <p className="font-bold text-sm">
                                {order.orderType === 'buffet' 
                                  ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                  : order.orderType === 'dine-in' 
                                  ? (order.tableName || (() => {
                                      const table = tables.find(t => t.id === order.tableId);
                                      return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                    })())
                                  : order.orderType === 'takeaway' 
                                  ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`) 
                                  : order.orderType === 'online'
                                  ? `Online Order`
                                  : order.orderType === 'event'
                                  ? `Event`
                                  : order.orderType === 'uber-doordash'
                                  ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                  : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                              </p>
                              {/* Only show customer name on second line when not already in title */}
                              {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                <p className="text-sm text-pink-600 font-semibold">
                                  {order.customerName || order.deliveryInfo?.customerName}
                                </p>
                              )}
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs text-gray-600">Order ID: {order.id.slice(-6)}</p>
                                {(() => {
                                  const raw: any =
                                    (order as any).pickupTime ??
                                    (order as any).pickup_time ??
                                    (order as any).pickupAt ??
                                    (order as any).pickup_at ??
                                    (order as any).pickupTimestamp ??
                                    (order as any).pickup_timestamp;
                                  let pickupTimeMs: number | null = null;

                                  if (typeof raw === 'number' && !Number.isNaN(raw)) {
                                    pickupTimeMs = raw < 1_000_000_000_000 ? raw * 1000 : raw;
                                  } else if (typeof raw === 'string') {
                                    const trimmed = raw.trim();
                                    if (/^\d+$/.test(trimmed)) {
                                      const n = parseInt(trimmed, 10);
                                      if (!Number.isNaN(n)) pickupTimeMs = n < 1_000_000_000_000 ? n * 1000 : n;
                                    } else {
                                      const parsed = Date.parse(trimmed);
                                      if (!Number.isNaN(parsed)) pickupTimeMs = parsed;
                                    }
                                  } else if (raw && typeof raw === 'object') {
                                    // Firestore Timestamp instance or plain object
                                    if (typeof raw.toMillis === 'function') {
                                      const ms = raw.toMillis();
                                      if (typeof ms === 'number' && !Number.isNaN(ms)) pickupTimeMs = ms;
                                    } else if (typeof raw.seconds === 'number') {
                                      pickupTimeMs = raw.seconds * 1000;
                                    }
                                  } else if (raw instanceof Date) {
                                    pickupTimeMs = raw.getTime();
                                  }

                                  if (pickupTimeMs == null || Number.isNaN(pickupTimeMs)) return null;
                                  const dt = new Date(pickupTimeMs);
                                  if (Number.isNaN(dt.getTime())) return null;
                                  const label = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                  return (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300">
                                      {`Pickup ${label}`}
                                    </span>
                                  );
                                })()}
                              </div>
                              {order.orderType === 'buffet' && (
                                <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded mt-1 inline-block">
                                  BUFFET
                                </span>
                              )}
                              {order.orderType === 'buffet' && order.buffetEndTime && (
                                <div className="mt-1">
                                  <BuffetTimer endTime={order.buffetEndTime} />
                                </div>
                              )}
                            </div>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${order.status === 'ready' ? 'bg-green-100 text-green-700' : order.status === 'preparing' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                              {order.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex-1 cursor-pointer" onClick={() => handleSelectPendingOrder(order)}>
                              <p className="text-xs text-gray-600">
                                {order.orderType === 'buffet' 
                                  ? `Buffet ${order.buffetType ? `(${order.buffetType})` : ''}${order.items.length > 0 ? ` + ${order.items.reduce((sum, item) => sum + item.quantity, 0)} items` : ''} • $${getOrderTotals(order).totalAmount.toFixed(2)}`
                                  : `${order.items.reduce((sum, item) => sum + item.quantity, 0)} items • $${getOrderTotals(order).totalAmount.toFixed(2)}`}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">Payment: {order.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedHistoryOrder(order); }}
                                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                Details
                              </button>
                              {order.orderType === 'buffet' && order.status === 'buffet' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEndBuffetSessionForOrder(order); }}
                                  disabled={isCompletingBuffet}
                                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-60"
                                >
                                  {isCompletingBuffet ? 'Ending...' : 'Complete early'}
                                </button>
                              )}
                              {order.orderType === 'buffet' && onExtendBuffetTime && order.buffetEndTime && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateBuffetExtension(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Extend
                                </button>
                              )}
                              {(order.orderType === 'dine-in' || order.orderType === 'buffet') && order.paymentStatus === 'unpaid' && order.status !== 'comped' && onTransferOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateTransfer(order); }}
                                  className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                                >
                                  Transfer
                                </button>
                              )}
                              {(order.paymentStatus === 'paid' || order.status === 'served' || order.status === 'ready') && onRemakeOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateRemake(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Remake
                                </button>
                              )}
                              {order.orderType === 'uber-doordash' && onUpdateOrderStatus && (
                                <>
                                  {order.status !== 'ready' && order.status !== 'served' && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onUpdateOrderStatus(order.id, 'ready');
                                        }}
                                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                      >
                                        Mark Ready
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setUberIssueOrder(order);
                                          setUberIssueSelectedItemIndices([]);
                                          setUberIssueReason('OUT_OF_STOCK');
                                          setUberIssueExplanation('');
                                        }}
                                        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                      >
                                        Report item issue
                                      </button>
                                    </>
                                  )}
                                  {order.status === 'ready' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdateOrderStatus(order.id, 'served');
                                      }}
                                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                    >
                                      Picked Up
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </main>
            )}
            
            {/* Floor Plan View */}
            {posView === 'floor' && activeView === 'floor' && (orderType === 'dine-in' || orderType === 'buffet') && (
                <div className="flex flex-col flex-1 overflow-hidden">
                    {/* Table Management Controls */}
                    <div className="p-4 bg-white border-b flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleToggleMergeMode}
                                className={`px-4 py-2 rounded transition-colors ${
                                    isMergeMode
                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {isMergeMode ? 'Cancel Merge Mode' : 'Merge Tables'}
                            </button>
                            {isMergeMode && (
                                <>
                                    <button
                                        onClick={handleMergeClick}
                                        disabled={selectedTableIds.length < 2}
                                        className={`px-4 py-2 rounded transition-colors ${
                                            selectedTableIds.length >= 2
                                                ? 'bg-green-500 text-white hover:bg-green-600'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                    >
                                        Merge Selected ({selectedTableIds.length})
                                    </button>
                                    {selectedTableIds.length > 0 && (
                                        <button
                                            onClick={() => setSelectedTableIds([])}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                        >
                                            Clear Selection
                                        </button>
                                    )}
                                </>
                            )}
                            {((!isMergeMode && selectedTableForSplit) ||
                              (isMergeMode && mergedPrimarySelectedInMergeMode)) && (
                                <button
                                    onClick={() => {
                                      const target =
                                        isMergeMode && mergedPrimarySelectedInMergeMode
                                          ? mergedPrimarySelectedInMergeMode
                                          : selectedTableForSplit;
                                      if (target) handleSplitTable(target.id);
                                    }}
                                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                    Split merged table:{' '}
                                    {(isMergeMode && mergedPrimarySelectedInMergeMode
                                      ? mergedPrimarySelectedInMergeMode
                                      : selectedTableForSplit
                                    )?.name}
                                </button>
                            )}
                        </div>
                        {isMergeMode && (
                            <p className="text-sm text-gray-600">
                                Select tables to merge (click to select/deselect)
                                {mergedPrimarySelectedInMergeMode &&
                                  ' — or select only this merged table and use Split to unmerge.'}
                            </p>
                        )}
                        {!isMergeMode && selectedTableForSplit && (
                            <p className="text-sm text-gray-600">
                                Click on a merged table to split it
                            </p>
                        )}
                    </div>
                    <FloorPlanView 
                        tables={tables} 
                        orders={pendingOrdersSource} 
                        reservations={reservations} 
                        layoutComponents={layoutComponents} 
                        businessTimeZone={businessTimeZone}
                        onTableClick={handleSelectTableFromFloor}
                        isMergeMode={isMergeMode}
                        selectedTableIds={selectedTableIds}
                        onTableSelect={handleTableSelect}
                        onSplitTable={handleSplitTable}
                        onTransferOrder={handleInitiateTransfer}
                        onSeatNewParty={(table) => {
                          const tableActiveOrders = pendingOrdersSource.filter((o) =>
                            isFloorPlanActiveTableOrder(o, table.id, businessTimeZone),
                          );
                          const spareSeats = Math.max(0, table.capacity - getTableSeatsUsed(tableActiveOrders));
                          setPendingDineInTable(table);
                          setPendingDineInSpareSeats(spareSeats);
                          setShowDineInCoversModal(true);
                        }}
                        onSelectOrder={handleSelectPendingOrder}
                        onEndBuffetSession={handleEndBuffetSessionForOrder}
                    />
                </div>
            )}
            
            {/* Buffet: no menu — only table → cover → payment */}
            {posView === 'new-order' && orderType === 'buffet' && (
                <main className="flex-1 p-6 flex flex-col items-center justify-center bg-gray-50">
                  <p className="text-lg font-semibold text-gray-800 mb-2">Buffet — no items to order</p>
                  <p className="text-sm text-gray-600 mb-4">Select a table on the floor plan, then set covers and complete payment.</p>
                  <button
                    type="button"
                    onClick={() => { setPosView('floor'); setActiveView('floor'); }}
                    className="px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                  >
                    View floor plan
                  </button>
                </main>
            )}
            {/* Menu View (for new orders — not for buffet) */}
            {posView === 'new-order' && orderType && orderType !== 'buffet' && activeView === 'menu' && (
                <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
                    <CategoryList 
                        categories={categories}
                        selectedCategoryId={selectedCategoryId}
                        onSelectCategory={(id) => { setSelectedCategoryId(id); setMenuSearchQuery(''); }}
                        onMoveCategory={onMoveCategory}
                        onReorderCategory={onReorderCategory}
                    />
                    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                        <div className="flex-shrink-0 px-2 py-2 bg-white border-b border-gray-200">
                          <input
                            type="search"
                            placeholder="Search menu..."
                            value={menuSearchQuery}
                            onChange={(e) => setMenuSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            aria-label="Search menu items"
                          />
                        </div>
                        <QuickAddBar items={quickAddCombos} onAdd={handleAddComboToOrder} />
                        <MenuItemsGrid 
                            items={displayedItemsFiltered}
                            onAddItem={handleAddItemToOrder}
                            orders={orders}
                        />
                    </div>
                </div>
            )}
            
            {/* Pending Orders View: unpaid orders + takeaway paid but not yet picked up (today only for takeaway to avoid old completed orders) */}
            {posView === 'pending' && activeView === 'menu' && (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <h2 className="text-lg font-bold text-gray-800">Pending Orders</h2>
                    {pendingOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading pending orders…</p>
                    ) : (
                    <div className={POS_ORDER_LIST_GRID_CLASS}>
                      {pendingOrdersSource.filter(order => {
                        if (isSampleOrDemoOrderId(String(order.id))) return false;
                        return isPendingPosOrder(order);
                      }).map(order => (
                        <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 cursor-pointer" onClick={() => handleSelectPendingOrder(order)}>
                                <p className="font-bold text-sm">
                                {order.orderType === 'buffet' 
                                  ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                  : order.orderType === 'dine-in' 
                                  ? (order.tableName || (() => {
                                      const table = tables.find(t => t.id === order.tableId);
                                      return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                    })())
                                  : order.orderType === 'takeaway' 
                                  ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`) 
                                  : order.orderType === 'online'
                                  ? `Online Order`
                                  : order.orderType === 'event'
                                  ? `Event`
                                  : order.orderType === 'uber-doordash'
                                  ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                  : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                              </p>
                              {/* Only show customer name on second line when not already in title */}
                              {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                <p className="text-sm text-pink-600 font-semibold">
                                  {order.customerName || order.deliveryInfo?.customerName}
                                </p>
                              )}
                              <p className="text-xs text-gray-600">Order ID: {order.id.slice(-6)}</p>
                              {order.orderType === 'buffet' && (
                                <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded mt-1 inline-block">
                                  BUFFET
                                </span>
                              )}
                              {order.orderType === 'buffet' && order.buffetEndTime && (
                                <div className="mt-1">
                                  <BuffetTimer endTime={order.buffetEndTime} />
                                </div>
                              )}
                              <p className="text-xs text-gray-500">
                                {(() => {
                                  let orderDate: Date;
                                  if (typeof order.timestamp === 'number') {
                                    orderDate = new Date(order.timestamp);
                                  } else if (order.timestamp instanceof Date) {
                                    orderDate = order.timestamp;
                                  } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                    orderDate = new Date((order.timestamp as any).seconds * 1000);
                                  } else {
                                    return 'Invalid Date';
                                  }
                                  if (isNaN(orderDate.getTime())) {
                                    return 'Invalid Date';
                                  }
                                  return orderDate.toLocaleString('en-NZ', {
                                    timeZone: 'Pacific/Auckland',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                })()}
                              </p>
                            </div>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${order.status === 'ready' ? 'bg-green-100 text-green-700' : order.status === 'preparing' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                              {order.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex-1 cursor-pointer" onClick={() => handleSelectPendingOrder(order)}>
                              <p className="text-xs text-gray-600">
                                {order.orderType === 'buffet' 
                                  ? `Buffet ${order.buffetType ? `(${order.buffetType})` : ''}${order.items.length > 0 ? ` + ${order.items.reduce((sum, item) => sum + item.quantity, 0)} items` : ''} • $${getOrderTotals(order).totalAmount.toFixed(2)}`
                                  : `${order.items.reduce((sum, item) => sum + item.quantity, 0)} items • $${getOrderTotals(order).totalAmount.toFixed(2)}`}
                              </p>
                              <p className="text-xs mt-1 font-bold">{order.paymentStatus === 'paid' && order.orderType === 'takeaway' ? '✅ Paid • Awaiting pickup' : order.paymentStatus === 'paid' && order.orderType === 'online' && order.status !== 'served' ? '✅ Paid • Awaiting pickup' : order.paymentStatus === 'paid' && order.orderType === 'buffet' ? '✅ Paid • Session active' : order.paymentStatus === 'paid' && order.orderType === 'uber-doordash' ? '✅ Paid via platform' : '⏳ Payment Pending'}</p>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedHistoryOrder(order); }}
                                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                Details
                              </button>
                              {order.orderType === 'buffet' && order.status === 'buffet' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEndBuffetSessionForOrder(order); }}
                                  disabled={isCompletingBuffet}
                                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-60"
                                >
                                  {isCompletingBuffet ? 'Ending...' : 'Complete early'}
                                </button>
                              )}
                              {(order.orderType === 'takeaway' || order.orderType === 'online') && order.paymentStatus === 'paid' && order.status !== 'served' && onUpdateOrderStatus && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onUpdateOrderStatus(order.id, 'served');
                                  }}
                                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                >
                                  Order Picked Up
                                </button>
                              )}
                              {order.orderType === 'uber-doordash' && onUpdateOrderStatus && (
                                <>
                                  {!order.deliveryInfo?.uberReadySentAt && order.status !== 'served' && (
                                    <>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (order.deliveryInfo?.platform === 'Uber Eats') {
                                            await handleSendUberReady(order);
                                          } else {
                                            onUpdateOrderStatus(order.id, 'ready');
                                          }
                                        }}
                                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                      >
                                        Ready for Pick Up
                                      </button>
                                      {order.deliveryInfo?.platform === 'Uber Eats' && order.deliveryInfo?.orderId && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setUberIssueOrder(order);
                                            setUberIssueSelectedItemIndices([]);
                                            setUberIssueReason('OUT_OF_STOCK');
                                            setUberIssueExplanation('');
                                          }}
                                          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                        >
                                          Report item issue
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {order.deliveryInfo?.uberReadySentAt && order.status !== 'served' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkUberDoordashPickedUp(order);
                                      }}
                                      className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                    >
                                      Mark Picked Up
                                    </button>
                                  )}
                                </>
                              )}
                              {order.orderType === 'buffet' && onExtendBuffetTime && order.buffetEndTime && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateBuffetExtension(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Extend
                                </button>
                              )}
                              {(order.orderType === 'dine-in' || order.orderType === 'buffet') && order.paymentStatus === 'unpaid' && order.status !== 'comped' && onTransferOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateTransfer(order); }}
                                  className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                                >
                                  Transfer
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                </main>
            )}
            
            {/* Completed Orders View */}
            {posView === 'completed' && activeView === 'menu' && (() => {
              const completedOrdersSource = mergeOrderSources(historyOrdersFetched, orders);

              // Filter orders based on selected filter
              const getFilteredCompletedOrders = () => {
                const baseFiltered = completedOrdersSource.filter((order) => {
                  if (isSampleOrDemoOrderId(String(order.id))) return false;
                  return isCompletedPosOrder(order);
                });
                return filterOrdersByHistoryDate(
                  baseFiltered,
                  historyOrdersDateFilter,
                  historyDateFilterOpts,
                );
              };

              let filteredOrders = getFilteredCompletedOrders();
              if (completedOrderTypeFilter !== 'all') {
                filteredOrders = filteredOrders.filter(order => order.orderType === completedOrderTypeFilter);
              }

              return (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-800">Completed Orders (Paid)</h2>
                      <HistoryOrdersDateFilterBar
                        filter={historyOrdersDateFilter}
                        onFilterChange={setHistoryOrdersDateFilter}
                        customDateStart={customDateStart}
                        customDateEnd={customDateEnd}
                        onCustomDateStartChange={setCustomDateStart}
                        onCustomDateEndChange={setCustomDateEnd}
                        extraControls={
                          <>
                            <span className="text-xs font-medium text-gray-500 mr-1">Type:</span>
                            <select
                              value={completedOrderTypeFilter}
                              onChange={(e) => setCompletedOrderTypeFilter(e.target.value as OrderType | 'all')}
                              className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white"
                            >
                              <option value="all">All</option>
                              <option value="dine-in">Dine-in</option>
                              <option value="takeaway">Takeaway</option>
                              <option value="online">Online</option>
                              <option value="delivery">Delivery</option>
                              <option value="uber-doordash">Uber/DoorDash</option>
                              <option value="buffet">Buffet</option>
                              <option value="event">Event</option>
                            </select>
                          </>
                        }
                      />
                    </div>
                    {historyOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading orders…</p>
                    ) : filteredOrders.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No completed orders found for the selected period.
                      </p>
                    ) : (
                      <div className={POS_ORDER_LIST_GRID_CLASS}>
                        {filteredOrders.map(order => (
                        <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-green-500 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 cursor-pointer" onClick={() => { setSelectedHistoryOrder(order); }}>
                              <p className="font-bold text-sm flex items-center gap-2">
                                {order.orderType === 'buffet'
                                  ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                  : order.orderType === 'dine-in'
                                  ? (order.tableName || (() => {
                                      const table = tables.find(t => t.id === order.tableId);
                                      return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                    })())
                                  : order.orderType === 'takeaway'
                                  ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`)
                                  : order.orderType === 'online'
                                  ? `Online Order`
                                  : order.orderType === 'event'
                                  ? `Event`
                                  : order.orderType === 'uber-doordash'
                                  ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                  : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                                {order.orderType === 'buffet' && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                    BUFFET
                                  </span>
                                )}
                                {order.orderType === 'event' && (
                                  <span className="text-xs font-bold px-2 py-0.5 bg-amber-100 text-amber-900 rounded tabular-nums">
                                    Ticket{' '}
                                    {formatEventTokenShortBadge(getEventTokenDisplayLabel(order, completedEventTokenLabelById) ?? '') || '—'}
                                  </span>
                                )}
                              </p>
                              {/* Only show customer name on second line when not already in title */}
                              {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                <p className="text-sm text-pink-600 font-semibold">{order.customerName || order.deliveryInfo?.customerName}</p>
                              )}
                              <p className="text-xs text-gray-600">Order ID: {order.id}</p>
                              {order.orderType === 'event' && (
                                <p className="text-xs font-semibold text-amber-900 mt-0.5">
                                  Ticket{' '}
                                  {formatEventTokenShortBadge(getEventTokenDisplayLabel(order, completedEventTokenLabelById) ?? '') || '—'}
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {(() => {
                                  let orderDate: Date;
                                  if (typeof order.timestamp === 'number') {
                                    orderDate = new Date(order.timestamp);
                                  } else if (order.timestamp instanceof Date) {
                                    orderDate = order.timestamp;
                                  } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                    orderDate = new Date((order.timestamp as any).seconds * 1000);
                                  } else {
                                    return 'Invalid Date';
                                  }
                                  if (isNaN(orderDate.getTime())) {
                                    return 'Invalid Date';
                                  }
                                  return orderDate.toLocaleString('en-NZ', {
                                    timeZone: 'Pacific/Auckland',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                })()}
                              </p>
                            </div>
                            <span className="text-xs font-bold px-2 py-1 rounded bg-green-100 text-green-700">
                              PAID
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex-1 cursor-pointer" onClick={() => { setSelectedHistoryOrder(order); }}>
                              <p className="text-xs text-gray-600">{order.items.length} items • ${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                              <p className="text-xs text-green-600 mt-1 font-bold">✅ Completed</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedHistoryOrder(order); }}
                                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                Details
                              </button>
                              {(order.paymentStatus === 'paid' || order.status === 'served' || order.status === 'ready') && onRemakeOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateRemake(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Remake
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    )}
                  </div>
                </main>
              );
            })()}
            {/* Cancelled Orders View */}
            {posView === 'cancelled' && activeView === 'menu' && (() => {
              const cancelledSource = mergeOrderSources(historyOrdersFetched, orders);
              const filteredCancelled = filterOrdersByHistoryDate(
                cancelledSource.filter(
                  (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'cancelled',
                ),
                historyOrdersDateFilter,
                historyDateFilterOpts,
              );

              return (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-800">Cancelled Orders</h2>
                      <HistoryOrdersDateFilterBar
                        filter={historyOrdersDateFilter}
                        onFilterChange={setHistoryOrdersDateFilter}
                        customDateStart={customDateStart}
                        customDateEnd={customDateEnd}
                        onCustomDateStartChange={setCustomDateStart}
                        onCustomDateEndChange={setCustomDateEnd}
                      />
                    </div>
                    {historyOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading orders…</p>
                    ) : filteredCancelled.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No cancelled orders found for the selected period.
                      </p>
                    ) : (
                      <div className={POS_ORDER_LIST_GRID_CLASS}>
                        {filteredCancelled.map(order => (
                          <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-red-500 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { setSelectedHistoryOrder(order); }}>
                                <p className="font-bold text-sm">
                                  {order.orderType === 'buffet'
                                    ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                    : order.orderType === 'dine-in'
                                    ? (order.tableName || (() => {
                                        const table = tables.find(t => t.id === order.tableId);
                                        return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                      })())
                                    : order.orderType === 'takeaway'
                                    ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`)
                                    : order.orderType === 'online'
                                    ? `Online Order`
                                    : order.orderType === 'event'
                                    ? `Event`
                                    : order.orderType === 'uber-doordash'
                                    ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                    : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                                </p>
                                {/* Only show customer name on second line when not already in title (e.g. Uber/DoorDash, dine-in) */}
                                {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                  <p className="text-sm text-pink-600 font-semibold">{order.customerName || order.deliveryInfo?.customerName}</p>
                                )}
                                <p className="text-xs text-gray-600">Order ID: {order.id}</p>
                                <p className="text-xs text-gray-500">
                                  {(() => {
                                    let orderDate: Date;
                                    if (typeof order.timestamp === 'number') {
                                      orderDate = new Date(order.timestamp);
                                    } else if (order.timestamp instanceof Date) {
                                      orderDate = order.timestamp;
                                    } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                      orderDate = new Date((order.timestamp as any).seconds * 1000);
                                    } else {
                                      return 'Invalid Date';
                                    }
                                    if (isNaN(orderDate.getTime())) {
                                      return 'Invalid Date';
                                    }
                                    return orderDate.toLocaleString('en-NZ', {
                                      timeZone: 'Pacific/Auckland',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                  })()}
                                </p>
                              </div>
                              <span className="text-xs font-bold px-2 py-1 rounded bg-red-100 text-red-700">
                                CANCELLED
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { setSelectedHistoryOrder(order); }}>
                                <p className="text-xs text-gray-600">{order.items.length} items • ${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                                <p className="text-xs text-red-600 mt-1 font-bold">⚠️ Order cancelled</p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSelectedHistoryOrder(order); }}
                                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                >
                                  Details
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </main>
              );
            })()}
        </div>
        {showOrderSummary && (
        <OrderSummary 
            key={selectedTable?.id ?? 'no-table'}
            orderItems={orderItems}
            notes={notes}
            setNotes={setNotes}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onUpdateItemNote={handleUpdateItemNote}
            onSendToKitchen={handleSendToKitchen}
            onUpdateOrder={handleUpdateOrder}
            onCancelOrder={handleCancelOrder}
            onReleaseTable={handleReleaseTable}
            onInitiatePayment={handleInitiatePayment}
            onClearOrder={handleNewOrder} // Clear order now always goes to new order state
            onNewOrder={handleNewOrder}
            onSelectCustomer={() => setIsCustomerModalOpen(true)}
            onSelectTable={() => setActiveView('floor')}
            onRedeemReward={() => setIsRewardModalOpen(true)}
            selectedCustomer={selectedCustomer}
            selectedTable={selectedTable}
            selectedServingStaff={selectedServingStaff}
            staffMembers={staffMembers}
            currentUser={currentUser}
            onSelectServingStaff={setSelectedServingStaff}
            orderType={orderType || 'dine-in'}
            setOrderType={(type) => {
              if (type !== 'uber-doordash') setUberDoordashDetails(null);
              if (type !== 'takeaway' && type !== 'delivery') setOrderCustomerName(null);
              if (type !== 'delivery') setDeliveryCharge(0);
              setOrderType(type);
              if (type === 'dine-in' && !selectedTable) {
                setActiveView('floor');
              }
            }}
            appliedReward={appliedReward}
            onRemoveReward={() => setAppliedReward(null)}
            isSent={!!placedOrder}
            isPaid={orderFullySettled}
            onPrintReceipt={() => resolvedPlacedOrder && handlePrintReceipt(resolvedPlacedOrder)}
            rewards={rewards}
            orderStatus={resolvedPlacedOrder?.status}
            manualDiscount={manualDiscount}
            discountRequiresApproval={discountRequiresApproval}
            discountApprovedBy={discountApprovedBy}
            discountApprovalReason={discountApprovalReason}
            discountMethod={discountMethod}
            firstTableDiscountAmount={firstTableDiscountAmount}
            onApplyDiscount={() => setIsDiscountModalOpen(true)}
            onRemoveDiscount={handleRemoveDiscount}
            onRemake={resolvedPlacedOrder && orderFullySettled && resolvedPlacedOrder.orderType !== 'buffet' ? () => handleInitiateRemake(resolvedPlacedOrder) : undefined}
            onTransfer={resolvedPlacedOrder && (resolvedPlacedOrder.orderType === 'dine-in' || resolvedPlacedOrder.orderType === 'buffet') && resolvedPlacedOrder.paymentStatus === 'unpaid' && resolvedPlacedOrder.status !== 'comped' ? () => handleInitiateTransfer(resolvedPlacedOrder) : undefined}
            currentOrder={resolvedPlacedOrder || undefined}
            isManager={isManager}
            buffetPricing={integrationSettings?.buffetPricing}
            selectedBuffetType={selectedBuffetType}
            onContinueBuffet={orderType === 'buffet' && selectedBuffetType ? handleOpenBuffetPeopleModal : undefined}
            uberDoordashPriceIncreasePercent={integrationSettings?.uberDoordashPriceIncreasePercent ?? 0}
            orderCustomerDisplayName={orderType === 'uber-doordash' ? (resolvedPlacedOrder?.deliveryInfo?.customerName ?? resolvedPlacedOrder?.customerName ?? uberDoordashDetails?.customerName ?? null) : (orderType === 'takeaway' || orderType === 'delivery') ? (resolvedPlacedOrder?.customerName ?? orderCustomerName ?? null) : null}
            pendingDeliveryCharge={deliveryCharge}
            onPendingDeliveryChargeChange={setDeliveryCharge}
            onExtendBuffetTime={resolvedPlacedOrder && resolvedPlacedOrder.orderType === 'buffet' ? () => handleInitiateBuffetExtension(resolvedPlacedOrder) : undefined}
            onCompleteBuffet={resolvedPlacedOrder && resolvedPlacedOrder.orderType === 'buffet' && resolvedPlacedOrder.status === 'buffet' ? handleCompleteBuffetOrder : undefined}
            isCompletingBuffet={isCompletingBuffet}
            onMarkAsPaid={isManager && resolvedPlacedOrder && (resolvedPlacedOrder.paymentStatus === 'unpaid' || hasBalanceDue(resolvedPlacedOrder)) ? () => onMarkAsPaid?.(resolvedPlacedOrder.id) : undefined}
            onMarkPickedUp={
              resolvedPlacedOrder &&
              (resolvedPlacedOrder.orderType === 'takeaway' || resolvedPlacedOrder.orderType === 'online') &&
              resolvedPlacedOrder.paymentStatus === 'paid' &&
              resolvedPlacedOrder.status !== 'served' &&
              onUpdateOrderStatus
                ? () => onUpdateOrderStatus(resolvedPlacedOrder.id, 'served')
                : undefined
            }
            onMarkUberPickedUp={
              resolvedPlacedOrder?.orderType === 'uber-doordash' && resolvedPlacedOrder.status !== 'served'
                ? () => handleMarkUberDoordashPickedUp(resolvedPlacedOrder)
                : undefined
            }
            onMarkUberReady={
              resolvedPlacedOrder?.orderType === 'uber-doordash' &&
              !resolvedPlacedOrder.deliveryInfo?.uberReadySentAt &&
              resolvedPlacedOrder.status !== 'served'
                ? () => handleSendUberReady(resolvedPlacedOrder)
                : undefined
            }
        />
        )}
        {uberPendingOrders.length > 0 && (
          <div className="border-t border-gray-200 px-3 pb-4 pt-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Uber / DoorDash – Incoming</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {uberPendingOrders.map((order) => (
                <div key={order.id} className="border rounded-md px-3 py-2 bg-green-50 border-green-300">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-green-800">Uber Eats Order</span>
                    <span className="text-[11px] font-mono text-gray-600 truncate">#{order.platformOrderId}</span>
                  </div>
                  <p className="text-xs text-gray-700 mb-1">
                    Customer: <span className="font-medium">{order.customerName}</span>
                  </p>
                  <ul className="text-xs text-gray-700 mb-2">
                    {order.items.map((item, idx) => (
                      <li key={idx}>
                        {item.quantity}x {item.name}
                        {item.specialInstructions && (
                          <span className="text-[11px] text-gray-500"> – {item.specialInstructions}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onRejectUberDeliveryOrder && onRejectUberDeliveryOrder(order)}
                      className="flex-1 px-2 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => onAcceptUberDeliveryOrder && onAcceptUberDeliveryOrder(order)}
                      className="flex-1 px-2 py-1 rounded-md text-xs font-semibold bg-green-600 text-white hover:bg-green-700"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
      {isCustomerModalOpen && (
        <CustomerSelectModal
          customers={customers}
          onSelect={handleSelectCustomer}
          onClose={() => setIsCustomerModalOpen(false)}
          onCreateCustomer={onCreateCustomer}
        />
      )}
      {uberIssueOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl border border-gray-200">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">Report item issue (Uber Eats)</h3>
                <p className="text-xs text-gray-500 truncate">
                  Order: {uberIssueOrder.id} • Uber ID: {uberIssueOrder.deliveryInfo?.orderId || '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (isSubmittingUberIssue) return;
                  setUberIssueOrder(null);
                }}
                className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Select affected items</p>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {uberIssueOrder.items.map((it, idx) => {
                    const checked = uberIssueSelectedItemIndices.includes(idx);
                    return (
                      <label key={idx} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setUberIssueSelectedItemIndices((prev) => {
                              if (prev.includes(idx)) return prev.filter((x) => x !== idx);
                              return [...prev, idx];
                            });
                          }}
                        />
                        <span className="text-sm text-gray-800">
                          {it.quantity}x {it.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Reason</label>
                  <select
                    value={uberIssueReason}
                    onChange={(e) => setUberIssueReason(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="OUT_OF_STOCK">Out of stock</option>
                    <option value="ITEM_INCORRECT">Item incorrect</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
                  <input
                    value={uberIssueExplanation}
                    onChange={(e) => setUberIssueExplanation(e.target.value)}
                    maxLength={200}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="Short explanation"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">{uberIssueExplanation.length}/200</p>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isSubmittingUberIssue) return;
                  setUberIssueOrder(null);
                }}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingUberIssue}
                onClick={handleSubmitUberIssue}
                className="px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {isSubmittingUberIssue ? 'Sending…' : 'Send to Uber'}
              </button>
            </div>
          </div>
        </div>
      )}
       {isRewardModalOpen && selectedCustomer && (
        <RewardRedemptionModal
          customer={selectedCustomer}
          rewards={rewards}
          onSelect={handleSelectReward}
          onClose={() => setIsRewardModalOpen(false)}
        />
      )}
      {isDiscountModalOpen && (
        <DiscountModal
          isOpen={isDiscountModalOpen}
          onClose={() => setIsDiscountModalOpen(false)}
          onApplyDiscount={handleApplyDiscount}
          currentTotal={(() => {
            const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
            const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
            return totalBeforeDiscount - rewardDiscount;
          })()}
          existingDiscount={manualDiscount}
          onApplyCoupon={handleApplyCoupon}
          couponError={couponError}
          activeCouponCode={
            couponCode
              ? couponUnitLabel
                ? `${couponUnitLabel} (${couponCode})`
                : couponCode
              : null
          }
          hasPaidSubBills={(placedOrder?.subBills?.length ?? 0) > 0}
          firstTableConfig={(() => {
            const cfg = integrationSettings?.firstTable;
            const hasCategoryRules = (cfg?.eligibleCategoryIds?.length ?? 0) > 0;
            const hasItemRules = (cfg?.eligibleMenuItemIds?.length ?? 0) > 0;
            if (!cfg?.enabled || !cfg.discountPercent || (!hasCategoryRules && !hasItemRules)) {
              return undefined;
            }
            const eligibleCategoryIds = (cfg.eligibleCategoryIds ?? []).map((id) => String(id));
            const eligibleMenuItemIds = new Set<number>(cfg.eligibleMenuItemIds ?? []);
            const percent = cfg.discountPercent;
            const orderItemsForDiscount = orderItems
              .filter((item) => !item.removed)
              .map((item) => {
                const originalTotal = item.price * item.quantity;
                const isEligible =
                  eligibleMenuItemIds.has(item.id as number) ||
                  eligibleCategoryIds.includes(String(item.categoryId));
                return {
                  name: item.name,
                  quantity: item.quantity,
                  originalTotal,
                  isEligible,
                };
              });
            const eligibleItems = orderItemsForDiscount
              .filter((item) => item.isEligible)
              .map((item) => ({
                name: item.name,
                quantity: item.quantity,
                originalTotal: item.originalTotal,
                discountedTotal: item.originalTotal * (1 - percent / 100),
              }));
            const eligibleSubtotal = eligibleItems.reduce((sum, it) => sum + it.originalTotal, 0);
            const eligibleCategoryNames = categories
              .filter((c) => eligibleCategoryIds.includes(String(c.id)))
              .map((c) => c.name)
              .sort((a, b) => a.localeCompare(b));
            return {
              enabled: true,
              discountPercent: cfg.discountPercent,
              eligibleSubtotal,
              eligibleCategoryNames,
              eligibleItems,
              orderItems: orderItemsForDiscount,
            };
          })()}
        />
      )}
      {isApprovalModalOpen && (
        <ManagerApprovalModal
          isOpen={isApprovalModalOpen}
          onClose={() => setIsApprovalModalOpen(false)}
          onApprove={handleApproveDiscount}
          discountAmount={pendingDiscount}
          discountPercentage={(() => {
            const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
            const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
            const subtotal = totalBeforeDiscount - rewardDiscount;
            return subtotal > 0 ? (pendingDiscount / subtotal) * 100 : 0;
          })()}
          orderTotal={(() => {
            const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
            const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
            return totalBeforeDiscount - rewardDiscount;
          })()}
          staffMembers={staffMembers}
          currentUser={currentUser}
        />
      )}
      {isPaymentModalOpen && resolvedPlacedOrder && (
        <PaymentModal
          order={resolvedPlacedOrder}
          customer={resolvedPlacedOrder.customerId ? (customers.find(c => c.id === resolvedPlacedOrder.customerId) || null) : null}
          terminals={terminals}
          onClose={handleClosePaymentModal}
          onProcessPayment={onProcessPayment}
          onPrintReceipt={handlePrintReceipt}
          onNewOrder={handleFinalNewOrder}
          onSyncPaymentStatus={onMarkAsPaid}
          integrationSettings={integrationSettings}
          onPrintSubBillPreview={(printOrder, preview) => {
            setSubBillToPrint({ order: printOrder, preview });
          }}
          onReprintSubBill={(printOrder, sb) => {
            setSubBillToPrint({ order: printOrder, subBill: sb });
          }}
        />
      )}
      {selectedHistoryOrder && (
        <OrderDetailsModal
          order={selectedHistoryOrder}
          customer={historyOrderCustomer}
          terminals={terminals}
          integrationSettings={integrationSettings}
          onClose={() => setSelectedHistoryOrder(null)}
          onPrintReceipt={handlePrintReceipt}
          onReprintSubBill={(printOrder, sb) => {
            setSubBillToPrint({ order: printOrder, subBill: sb });
          }}
          onRefundSubBill={onRefundSubBill}
          onOverride={handleInitiateOverride}
          onRemake={handleInitiateRemake}
          onTransfer={handleInitiateTransfer}
          onExtendBuffetTime={selectedHistoryOrder?.orderType === 'buffet' && selectedHistoryOrder?.buffetEndTime ? (order) => handleInitiateBuffetExtension(order) : undefined}
          onCompleteBuffetOrder={selectedHistoryOrder?.orderType === 'buffet' && selectedHistoryOrder?.status === 'buffet' ? (order) => handleEndBuffetSessionForOrder(order) : undefined}
          onPayBalance={(order) => {
            const liveOrder = orders.find(o => o.id === order.id) || order;
            setPlacedOrder(liveOrder);
            setSelectedHistoryOrder(null);
            setIsPaymentModalOpen(true);
          }}
          isManager={isManager}
          onRefund={async (orderId, refundAmount, refundTransactionId) => {
            // Find the order and add a refund payment
            const order = orders.find(o => o.id === orderId);
            if (!order) return;

            const isCashRefund = refundTransactionId === 'cash';
            const now = Date.now();
            const refundPayment: Payment = {
              method: isCashRefund ? 'cash' : 'card',
              amount: -refundAmount, // Negative amount for refund
              timestamp: now,
              ...(isCashRefund
                ? {}
                : {
                    transactionId: refundTransactionId,
                    refundTransactionId: refundTransactionId,
                  }),
            };

            const updatedPayments = [...order.payments, refundPayment];
            const updatedOrder = {
              ...order,
              payments: updatedPayments,
              paymentStatus: 'paid',
              status: 'refunded' as const,
            };
            await onUpdateOrder(updatedOrder);

            // Audit log for refund (cash or card)
            try {
              const actor = currentUser;
              await auditLogsService.add({
                timestamp: now,
                actorId: actor?.id,
                actorName: actor?.name,
                actorRole: actor?.role,
                action: 'order.refund',
                entityType: 'order',
                entityId: orderId,
                details: {
                  method: isCashRefund ? 'cash' : 'card',
                  amount: refundAmount,
                  refundTransactionId,
                  previousPaymentsCount: order.payments.length,
                },
              });
            } catch (e) {
              console.error('Failed to write refund audit log', e);
            }

            alert(`Refund of $${refundAmount.toFixed(2)} has been processed and recorded.`);
          }}
        />
      )}
      {isConfirmationDisplayOpen && orderType && (
        <CustomerConfirmationDisplay
          orderItems={orderItems}
          subtotal={orderSubtotal}
          taxAmount={orderTax}
          totalAmount={orderTotal}
          appliedReward={appliedReward}
          notes={notes}
          orderType={orderType}
          deliveryCharge={orderType === 'delivery' ? Math.max(0, deliveryCharge) : 0}
          onConfirm={handleConfirmOrder}
          onCancel={handleCancelConfirmation}
          isPlacingOrder={isPlacingOrder}
        />
      )}
      {showUberDoordashModal && (
        <UberDoordashOrderModal
          onConfirm={handleUberDoordashModalSubmit}
          onCancel={() => setShowUberDoordashModal(false)}
        />
      )}
      {showCustomerNameModal && pendingOrderTypeForCustomerName && (
        <CustomerNameModal
          orderTypeLabel={pendingOrderTypeForCustomerName === 'takeaway' ? 'Takeaway' : 'Delivery'}
          onConfirm={handleCustomerNameModalSubmit}
          onCancel={() => {
            setShowCustomerNameModal(false);
            setPendingOrderTypeForCustomerName(null);
          }}
        />
      )}
       {printPendingOrder && (
          <PrintTypeModal
            order={printPendingOrder}
            onChoose={handlePrintTypeChosen}
            onCancel={() => setPrintPendingOrder(null)}
          />
       )}
       {orderToPrint && (
          printType === 'customer' ? (
            <PrintableReceipt ref={printRef} order={orderToPrint} businessInfo={integrationSettings?.businessInfo} />
          ) : (
            <PrintableKitchenOrder ref={printRef} order={orderToPrint} businessInfo={integrationSettings?.businessInfo} />
          )
       )}
       {subBillToPrint && (
          <PrintableReceipt
            ref={printRef}
            order={subBillToPrint.order}
            businessInfo={integrationSettings?.businessInfo}
            subBill={subBillToPrint.subBill}
            subBillPreview={subBillToPrint.preview}
          />
       )}
       {meatOptionModalItem && (
         <MeatOptionModal
           item={meatOptionModalItem}
           onSelect={handleMeatOptionSelected}
           onCancel={() => setMeatOptionModalItem(null)}
         />
       )}
       {showMergeModal && (
         <TableMergeModal
           selectedTables={
             selectedTableIds
               .map(id => tables.find(t => Number(t.id) === Number(id)))
               .filter(Boolean) as Table[]
           }
           orders={orders}
           onConfirm={handleConfirmMerge}
           onCancel={() => {
             setShowMergeModal(false);
             setSelectedTableIds([]);
           }}
         />
       )}
       {showSplitModal && tableToSplit && (
         <TableSplitModal
           mergedTable={tableToSplit}
           originalTables={tableToSplit.mergedWith
             ? tableToSplit.mergedWith.map(id => tables.find(t => Number(t.id) === Number(id))).filter(Boolean) as Table[]
             : []}
           orders={orders}
           onConfirm={handleConfirmSplit}
           onCancel={() => {
             setShowSplitModal(false);
             setTableToSplit(null);
           }}
         />
       )}
       {showTransferModal && orderToTransfer && (
         <OrderTransferModal
           sourceTable={orderToTransfer.table}
           order={orderToTransfer.order}
           availableTables={tables.filter(t => 
             t.status === 'available' && 
             t.id !== orderToTransfer.table.id &&
             (!t.mergedWith || t.mergedWith.length <= 1)
           )}
           onConfirm={handleConfirmTransfer}
           onCancel={() => {
             setShowTransferModal(false);
             setOrderToTransfer(null);
           }}
         />
       )}
       {showOverrideModal && orderToOverride && onOverrideOrder && (
         <OrderOverrideModal
           order={orderToOverride}
           overrideReasons={integrationSettings?.overrideReasons || []}
           onConfirm={handleConfirmOverride}
           onCancel={() => {
             setShowOverrideModal(false);
             setOrderToOverride(null);
           }}
         />
       )}
       {showRemakeModal && orderToRemake && onRemakeOrder && (
         <RemakeOrderModal
           order={orderToRemake}
           onConfirm={handleConfirmRemake}
           onCancel={() => {
             setShowRemakeModal(false);
             setOrderToRemake(null);
           }}
         />
       )}
       {showCancelApprovalModal && pendingCancelOrder && staffMembers && (
         <CancelOrderApprovalModal
           isOpen={showCancelApprovalModal}
           onClose={() => {
             setShowCancelApprovalModal(false);
             setPendingCancelOrder(null);
             setCancelReason('');
           }}
           onApprove={handleCancelApproval}
           order={pendingCancelOrder}
           staffMembers={staffMembers}
           currentUser={currentUser}
           requiresApproval={requiresManagerApproval(pendingCancelOrder)}
           cancellationReason={cancelReason}
         />
       )}
       {showBuffetExtensionModal && orderToExtend && onExtendBuffetTime && (
         <BuffetExtensionModal
           order={orderToExtend}
           currentUser={currentUser}
           isManager={currentUser?.permissions?.has('manager') || false}
           onConfirm={handleConfirmBuffetExtension}
           onCancel={() => {
             setShowBuffetExtensionModal(false);
             setOrderToExtend(null);
           }}
         />
       )}
       {showFireModeModal && onActivateFireMode && onDeactivateFireMode && (
         <FireModeModal
           isActive={fireModeActive || false}
           orderAction={integrationSettings?.fireMode?.orderAction || 'pause'}
           onActivate={handleConfirmFireModeActivate}
           onDeactivate={handleConfirmFireModeDeactivate}
           onCancel={() => setShowFireModeModal(false)}
         />
       )}
       {showTableCustomerModal && pendingTableSelection && (() => {
         const tableActiveOrders = orders.filter(o => {
           if (o.tableId !== pendingTableSelection.id) return false;
           if (o.status === 'cancelled' || o.status === 'refunded') return false;
           if (!orderHasMeaningfulItems(o)) return false;
           const todayKey = new Date().toDateString();
           const orderDateKey = new Date(o.timestamp).toDateString();
           if (orderDateKey !== todayKey) return false;
           return (o.orderType === 'dine-in' && o.paymentStatus === 'unpaid') ||
             (o.orderType === 'buffet' && o.status === 'buffet');
         });
         const spareSeats = Math.max(0, pendingTableSelection.capacity - getTableSeatsUsed(tableActiveOrders));
         return (
           <TableCustomerSelectModal
             table={pendingTableSelection}
             activeOrders={tableActiveOrders}
             spareSeats={spareSeats}
             onSelectCustomer={handleSelectTableCustomer}
             onClose={() => {
               setShowTableCustomerModal(false);
               setPendingTableSelection(null);
             }}
           />
         );
       })()}
       {showBuffetTypeSelector && (
         <BuffetTypeSelector
           prices={integrationSettings?.buffetPricing}
           onSelect={handleSelectBuffetType}
           onCancel={() => {
             setShowBuffetTypeSelector(false);
             setOrderType(null);
           }}
         />
       )}
       {showDineInCoversModal && pendingDineInTable && (
         <DineInCoversModal
           tableName={pendingDineInTable.name}
           maxSeats={pendingDineInSpareSeats || pendingDineInTable.capacity}
           onConfirm={handleSeatNewDineInParty}
           onCancel={() => {
             setShowDineInCoversModal(false);
             setPendingDineInTable(null);
             setPendingDineInSpareSeats(0);
           }}
         />
       )}
       {showBuffetPeopleModal && selectedBuffetType && integrationSettings?.buffetPricing && (
         <BuffetPeopleModal
           buffetType={selectedBuffetType}
           price={integrationSettings.buffetPricing[selectedBuffetType] || 0}
           onConfirm={handleConfirmBuffetPeople}
           onCancel={() => {
            setShowBuffetPeopleModal(false);
           }}
         />
       )}
    </>
  );
};

export default POSPage;